import React, { useState, useEffect, useCallback, useMemo } from "react";
import env from "../../config/env.js";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography, TableSortLabel,
  Tooltip, Select, MenuItem, InputLabel, FormControl, Snackbar, Alert,
  TablePagination, CircularProgress, FormControlLabel, Switch,
  Tabs, Tab, Autocomplete, useTheme
} from "@mui/material";
import { 
  Add as AddIcon, 
  Delete as DeleteIcon, 
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  School as SchoolIcon,
  Print as PrintIcon
} from "@mui/icons-material";
import AcessoRestrito from '../../components/AcessoRestrito';
import api from "../../services/api";
import { 
  formatarMoeda, 
  removerFormatacao 
} from "../../utils/formatadores";
import { 
  formatarCPFCNPJLGPD,
  formatarTelefoneLGPD,
  formatarEmailLGPD
} from "../../utils/lgpd";

const drawerWidth = 280;

export default function Matriculas() {
  // Estados principais
  const [matriculas, setMatriculas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [clientesOrdenados, setClientesOrdenados] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [servicos, setServicos] = useState([]);
  const [locais, setLocais] = useState([]);
  const [colaboradores, setColaboradores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Estados para modais
  const [openCreate, setOpenCreate] = useState(false);

  const [openView, setOpenView] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [matriculaSelecionada, setMatriculaSelecionada] = useState(null);
  
  // Estados para formulários
  const [formData, setFormData] = useState({
    id_cliente: '',
    codigoTurma: '',
    forma_pagamento: '',
    valor_matricula: '',
    valor_total_servico: '',
    valor_servico: '',
    parcelamento: '',
    valor_parcela: '',
    observacoes: '',
    descricao_servico: '',
    // Novos campos do cliente
    data_nascimento: '',
    cpf_cnpj: '',
    rg: '',
    profissao: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    pessoa_contato: '',
    telefone_fixo: '',
    celular: '',
    email: '',
    eh_indicacao: false,
    desconto_percentual: '',
    desconto_valor: '',
    acrescimo_percentual: '',
    acrescimo_valor: '',
    // Novos campos da turma
    codigo_turma: '',
    diaInicio: '',
    diaFim: '',
    duracao_minuto: '',
    horaInicio: '',
    horaFim: '',
    diasDaSemana: '',
    local: '',
    colaborador: ''
  });
  const [loadingForm, setLoadingForm] = useState(false);
  
  // Estados para filtros e paginação
  const [filtros, setFiltros] = useState({
    id_cliente: '',
    codigoTurma: ''
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState("id");
  const [order, setOrder] = useState("asc");

  // Estado para tabs
  const [tabValue, setTabValue] = useState(0);

  // Estados para popup de validação de status do cliente
  const [openStatusPopup, setOpenStatusPopup] = useState(false);
  const [statusPopupData, setStatusPopupData] = useState({
    status: '',
    observacoes: ''
  });
  const [descricoesTurmas, setDescricoesTurmas] = useState({});

  // Estado para controlar se a matrícula foi impressa
  const [matriculaImpressa, setMatriculaImpressa] = useState(false);


  // Ordenar clientes quando forem carregados
  useEffect(() => {
    if (Array.isArray(clientes) && clientes.length > 0) {
      const ordenados = [...clientes].sort((a, b) => {
        const nomeA = (a.nome_completo || a.nome || '').toLowerCase();
        const nomeB = (b.nome_completo || b.nome || '').toLowerCase();
        return nomeA.localeCompare(nomeB);
      });
      setClientesOrdenados(ordenados);
    } else {
      setClientesOrdenados([]);
    }
  }, [clientes]);

  // Carregar clientes
  const carregarClientes = useCallback(async () => {
    setLoadingClientes(true);
    try {
      const token = localStorage.getItem("token");
      const codHashEmpresa = localStorage.getItem("st_codHashEmpresa");

      if (!token) {
        console.error("Token não encontrado");
        return;
      }
      
      if (!codHashEmpresa) {
        console.error("st_codHashEmpresa não encontrado");
        return;
      }
      
      const headers = {
        'Authorization': `Bearer ${token}`,
        'x-second-key': env.SECOND_SECRET_KEY,
        'Content-Type': 'application/json'
      };
      
      const res = await api.get(`/clientes?st_codHashEmpresa=${codHashEmpresa}`, { headers });
      
      if (Array.isArray(res.data)) {
        setClientes(res.data);
      } else if (res.data && Array.isArray(res.data.data)) {
        // Caso a API retorne { data: [...] }
        setClientes(res.data.data);
      } else {
        console.error("Formato de resposta inválido:", res.data);
        setClientes([]);
      }
    } catch (err) {
      console.error("❌ Erro ao carregar clientes:", err);
      setClientes([]);
    } finally {
      setLoadingClientes(false);
    }
  }, []);

  // Carregar turmas
  const carregarTurmas = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const codHashEmpresa = localStorage.getItem("st_codHashEmpresa");
      const headers = {
        'Authorization': `Bearer ${token}`,
        'x-second-key': env.SECOND_SECRET_KEY
      };
      const res = await api.get(`/horarioAtendimento?st_codHashEmpresa=${codHashEmpresa}`, { headers });
      setTurmas(res.data || []);
    } catch (err) {
      console.error('Erro ao carregar turmas:', err);
    }
  }, []);

  const carregarServicos = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const codHashEmpresa = localStorage.getItem("st_codHashEmpresa");
      const headers = {
        'Authorization': `Bearer ${token}`,
        'x-second-key': env.SECOND_SECRET_KEY
      };
      const res = await api.get(`/servicos?st_codHashEmpresa=${codHashEmpresa}`, { headers });

      // Verificar se a resposta é paginada ou array direto
      if (res.data && res.data.dados && Array.isArray(res.data.dados)) {
        setServicos(res.data.dados);
      } else if (Array.isArray(res.data)) {
        setServicos(res.data);
      } else {
        setServicos([]);
      }
    } catch (err) {
      console.error("Erro ao carregar serviços:", err);
      setServicos([]);
    }
  }, []);

  const carregarLocais = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const codHashEmpresa = localStorage.getItem("st_codHashEmpresa");
      const headers = {
        'Authorization': `Bearer ${token}`,
        'x-second-key': env.SECOND_SECRET_KEY
      };
      const res = await api.get(`/local?st_codHashEmpresa=${codHashEmpresa}`, { headers });
      setLocais(res.data || []);
    } catch (err) {
      console.error("Erro ao carregar locais:", err);
    }
  }, []);

  const carregarColaboradores = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const codHashEmpresa = localStorage.getItem("st_codHashEmpresa");
      const headers = {
        'Authorization': `Bearer ${token}`,
        'x-second-key': env.SECOND_SECRET_KEY
      };
      const res = await api.get(`/colaboradores?st_codHashEmpresa=${codHashEmpresa}`, { headers });
      
      // Filtrar colaboradores pelo st_codHashEmpresa (como no módulo de colaboradores)
      const colaboradoresFiltrados = res.data.filter(colaborador => 
          colaborador.st_codHashEmpresa === codHashEmpresa
        );

      setColaboradores(colaboradoresFiltrados);
    } catch (err) {
      console.error("Erro ao carregar colaboradores:", err);
    }
  }, []);

  // Carregar matrículas
  const carregarMatriculas = useCallback(async (filtrosAtuais) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const codHashEmpresa = localStorage.getItem("st_codHashEmpresa");
      const headers = {
        'Authorization': `Bearer ${token}`,
        'x-second-key': env.SECOND_SECRET_KEY
      };
      
      let url = `/matricula?st_codHashEmpresa=${codHashEmpresa}`;
      if (filtrosAtuais?.id_cliente) url += `&id_cliente=${filtrosAtuais.id_cliente}`;
      if (filtrosAtuais?.codigoTurma) url += `&codigoTurma=${filtrosAtuais.codigoTurma}`;
      
      const res = await api.get(url, { headers });
      
      if (res.data?.length > 0) {
        console.log("📋 Dados da primeira matrícula:", {
          id: res.data[0].id_matricula || res.data[0].id, 
          cliente: res.data[0].id_cliente,
          turma: res.data[0].codigoTurma 
        });
      }
      
      setMatriculas(res.data || []);
    } catch (err) {
      setError("Erro ao carregar matrículas");
      console.error("Erro ao carregar matrículas:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Carregar dados apenas uma vez na inicialização
    const carregarDadosIniciais = async () => {
      await Promise.all([
        carregarMatriculas(),
        carregarClientes(),
        carregarTurmas(),
        carregarServicos(),
        carregarLocais(),
        carregarColaboradores()
      ]);
    };
    
    carregarDadosIniciais();
  }, []); // Carregar dados apenas uma vez na inicialização

  // Recarregar matrículas quando filtros mudarem (apenas se já tiver dados carregados)
  useEffect(() => {
    if (matriculas.length > 0) {
      carregarMatriculas(filtros);
    }
  }, [filtros]);

  // Carregar descrições das turmas quando as matrículas mudarem
  useEffect(() => {
    const carregarDescricoesTurmas = async () => {
      if (matriculas.length === 0) return;
      
      // Verificar se já temos as descrições carregadas para evitar requisições desnecessárias
      const codigosUnicos = [...new Set(matriculas.map(m => m.codigoTurma).filter(Boolean))];
      const codigosFaltantes = codigosUnicos.filter(codigo => !descricoesTurmas[codigo]);
      
      if (codigosFaltantes.length === 0) {
        return;
      }
      
      const descricoes = { ...descricoesTurmas };
      
      for (const codigoTurma of codigosFaltantes) {
        try {
          const token = localStorage.getItem("token");
          const headers = {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'x-second-key': env.SECOND_SECRET_KEY
          };
          
          const res = await api.get(`/horarioAtendimento/codigoTurma/${codigoTurma}`, { headers });
          
          if (res.data && res.data.nome) {
            descricoes[codigoTurma] = res.data.nome;
          } else {
            descricoes[codigoTurma] = codigoTurma; // Fallback
          }
        } catch (err) {
          console.error(`Erro ao buscar descrição da turma ${codigoTurma}:`, err);
          descricoes[codigoTurma] = codigoTurma; // Fallback
        }
      }
      
      setDescricoesTurmas(descricoes);
    };

    carregarDescricoesTurmas();
  }, [matriculas, descricoesTurmas]);

  // Função para obter nome do cliente
  const getNomeCliente = (clienteId) => {
    const cliente = clientes.find(c => {
      // Verificar diferentes possibilidades de ID
      const matchById = c.id === clienteId || c._id === clienteId;
      const matchByString = c.id?.toString() === clienteId?.toString() || c._id?.toString() === clienteId?.toString();
      
      return matchById || matchByString;
    });
    
    return cliente ? (cliente.nome || cliente.nome_completo) : clienteId;
  };

  // Função para obter status do cliente
  const getStatusCliente = (clienteId) => {
    const cliente = clientes.find(c => {
      // Verificar diferentes possibilidades de ID
      const matchById = c.id === clienteId || c._id === clienteId;
      const matchByString = c.id?.toString() === clienteId?.toString() || c._id?.toString() === clienteId?.toString();
      
      return matchById || matchByString;
    });
    
    if (!cliente) return 'N/A';
    
    // Verificar se o cliente tem status específico
    if (cliente.status) return cliente.status;
    
    // Se não tem status específico, verificar se é indicação
    if (cliente.eh_indicacao) return 'Indicação';
    
    // Status padrão baseado em outros campos
    if (cliente.ativo === false) return 'Inativo';
    if (cliente.ativo === true) return 'Ativo';
    
    return 'Ativo'; // Status padrão
  };

  // Função para validar status do cliente e mostrar popup se necessário
  const validarStatusCliente = (clienteId) => {
    const cliente = clientes.find(c => {
      const matchById = c.id === clienteId || c._id === clienteId;
      const matchByString = c.id?.toString() === clienteId?.toString() || c._id?.toString() === clienteId?.toString();
      return matchById || matchByString;
    });
    
    if (!cliente) return true; // Se não encontrar o cliente, permite continuar
    
    // Determinar o status do cliente
    let status = 'Ativo';
    if (cliente.status) {
      status = cliente.status;
    } else if (cliente.eh_indicacao) {
      status = 'Indicação';
    } else if (cliente.ativo === false) {
      status = 'Inativo';
    } else if (cliente.ativo === true) {
      status = 'Ativo';
    }
    
    // Se o status não for "Ativo", mostrar popup
    if (status.toLowerCase() !== 'ativo') {
      setStatusPopupData({
        status: status,
        observacoes: cliente.observacoes || 'Nenhuma observação disponível.'
      });
      setOpenStatusPopup(true);
      return false; // Impedir a matrícula
    }
    
    return true; // Permitir a matrícula
  };

  // Função para obter estilos do status baseado no valor
  const getStatusStyles = (status) => {
    const statusLower = status?.toLowerCase() || '';
    
    switch (statusLower) {
      case 'ativo':
        return {
          backgroundColor: '#e8f5e8',
          color: '#2e7d32',
          border: '1px solid #4caf50',
          padding: '4px 8px',
          borderRadius: '12px',
          fontSize: '0.68rem',
          fontWeight: 'bold',
          display: 'inline-block',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        };
      
      case 'inativo':
        return {
          backgroundColor: '#ffebee',
          color: '#d32f2f',
          border: '1px solid #f44336',
          padding: '4px 8px',
          borderRadius: '12px',
          fontSize: '0.68rem',
          fontWeight: 'bold',
          display: 'inline-block',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        };
      
      case 'indicação':
      case 'indicacao':
        return {
          backgroundColor: '#fff3e0',
          color: '#f57c00',
          border: '1px solid #ff9800',
          padding: '4px 8px',
          borderRadius: '12px',
          fontSize: '0.68rem',
          fontWeight: 'bold',
          display: 'inline-block',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        };
      
      case 'pendente':
        return {
          backgroundColor: '#e3f2fd',
          color: '#1976d2',
          border: '1px solid #2196f3',
          padding: '4px 8px',
          borderRadius: '12px',
          fontSize: '0.68rem',
          fontWeight: 'bold',
          display: 'inline-block',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        };
      
      case 'suspenso':
        return {
          backgroundColor: '#f3e5f5',
          color: '#7b1fa2',
          border: '1px solid #9c27b0',
          padding: '4px 8px',
          borderRadius: '12px',
          fontSize: '0.68rem',
          fontWeight: 'bold',
          display: 'inline-block',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        };
      
      case 'cancelado':
        return {
          backgroundColor: '#fafafa',
          color: '#616161',
          border: '1px solid #9e9e9e',
          padding: '4px 8px',
          borderRadius: '12px',
          fontSize: '0.68rem',
          fontWeight: 'bold',
          display: 'inline-block',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        };
      
      default:
        return {
          backgroundColor: '#f5f5f5',
          color: '#666',
          border: '1px solid #ddd',
          padding: '4px 8px',
          borderRadius: '12px',
          fontSize: '0.68rem',
          fontWeight: 'bold',
          display: 'inline-block',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        };
    }
  };

  // Função para obter nome da turma
    const getNomeTurma = (codigoTurma) => {
    const turma = turmas.find(t => t.codigoTurma === codigoTurma);
    return turma ? turma.nome : codigoTurma;
  };

  const getDescricaoTurma = (codigoTurma) => {
    // Primeiro tentar usar as descrições carregadas via API
    if (descricoesTurmas[codigoTurma]) {
      return descricoesTurmas[codigoTurma];
    }
    
    // Fallback para os dados locais
    const turma = turmas.find(t => t.codigoTurma === codigoTurma);
    return turma ? turma.nome || turma.descricao || 'N/A' : 'N/A';
  };

  // Função para buscar descrição da turma via API
  const getDescricaoTurmaAPI = async (codigoTurma) => {
    if (!codigoTurma) return 'N/A';
    
    try {
      const token = localStorage.getItem("token");
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-second-key': env.SECOND_SECRET_KEY
      };
      
      const res = await api.get(`/horarioAtendimento/codigoTurma/${codigoTurma}`, { headers });
      
      if (res.data && res.data.nome) {
        return res.data.nome;
      }
      
      return codigoTurma; // Fallback para o código da turma se não encontrar nome
    } catch (err) {
      console.error('Erro ao buscar descrição da turma:', err);
      return codigoTurma; // Fallback para o código da turma em caso de erro
    }
  };

  const getLocalTurma = (codigoTurma) => {
    const turma = turmas.find(t => t.codigoTurma === codigoTurma);
    if (!turma || !turma.local) return 'N/A';
    
    const local = locais.find(l => l.id === turma.local);
    return local ? local.nome : 'N/A';
  };

  const getDataInicioTurma = (codigoTurma) => {
    const turma = turmas.find(t => t.codigoTurma === codigoTurma);
    if (!turma || !turma.diaInicio) return 'N/A';
    
    try {
      const data = new Date(turma.diaInicio);
      return data.toLocaleDateString('pt-BR');
    } catch (error) {
      return 'N/A';
    }
  };

  const getDataTerminoTurma = (codigoTurma) => {
    const turma = turmas.find(t => t.codigoTurma === codigoTurma);
    if (!turma || !turma.diaFim) return 'N/A';
    
    try {
      const data = new Date(turma.diaFim);
      return data.toLocaleDateString('pt-BR');
    } catch (error) {
      return 'N/A';
    }
  };

  const getPeriodoTurma = (codigoTurma) => {
    const turma = turmas.find(t => t.codigoTurma === codigoTurma);
    if (!turma) return 'N/A';
    
    try {
      const dataInicio = turma.diaInicio ? new Date(turma.diaInicio).toLocaleDateString('pt-BR') : 'N/A';
      const dataFim = turma.diaFim ? new Date(turma.diaFim).toLocaleDateString('pt-BR') : 'N/A';
      
      if (dataInicio === 'N/A' && dataFim === 'N/A') return 'N/A';
      if (dataInicio === 'N/A') return dataFim;
      if (dataFim === 'N/A') return dataInicio;
      
      return `${dataInicio} - ${dataFim}`;
    } catch (error) {
      return 'N/A';
    }
  };

  // Resetar formulário
  const resetForm = () => {
    setFormData({
      id_cliente: '',
      codigoTurma: '',
      forma_pagamento: '',
      valor_matricula: '',
      valor_total_servico: '',
      valor_servico: '',
      parcelamento: '',
      valor_parcela: '',
      observacoes: '',
      descricao_servico: '',
      // Novos campos do cliente
      nome: '',
      data_nascimento: '',
      cpf_cnpj: '',
      rg: '',
      profissao: '',
      logradouro: '',
      numero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      estado: '',
      pessoa_contato: '',
      telefone_fixo: '',
      celular: '',
      email: '',
      eh_indicacao: false,
      desconto_percentual: '',
      desconto_valor: '',
      acrescimo_percentual: '',
      acrescimo_valor: '',
      // Novos campos da turma
      codigo_turma: '',
      diaInicio: '',
      diaFim: '',
      duracao_minuto: '',
      horaInicio: '',
      horaFim: '',
      diasDaSemana: '',
      local: '',
      colaborador: ''
    });
    
    // Garantir que os clientes estejam carregados para o Autocomplete
    if (clientes.length === 0) {
      carregarClientes();
    }
    
    // Resetar estado de impressão
    setMatriculaImpressa(false);
  };

  // Filtros
  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros(prev => ({ ...prev, [name]: value }));
    setPage(0);
  };

  // Ordenação
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Paginação
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Ações principais
  const handleAbrirCreate = () => {
    resetForm();
    setTabValue(0);
    setOpenCreate(true);
    
    // Garantir que os clientes estejam carregados para o Autocomplete
    if (clientes.length === 0) {
      carregarClientes();
    }
  };


  
  const handleAbrirView = (matricula) => {
    setMatriculaSelecionada(matricula);
    setOpenView(true);
  };
  
  const handleAbrirDelete = (matricula) => {
    
    if (!matricula) {
      console.error("❌ Matrícula é null/undefined em handleAbrirDelete");
      setError("Matrícula não encontrada");
      return;
    }
    
    setMatriculaSelecionada(matricula);
    setOpenDelete(true);
  };

  // Funções para formulários
  const handleFormChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };

      // Calcular valor da parcela automaticamente quando o parcelamento mudar
      if (name === 'parcelamento') {
        const valorMatricula = prev.valor_matricula;
        const valorTotalServico = prev.valor_total_servico;
        const parcelas = value;
        
        if (valorMatricula || valorTotalServico) {
          // Converter de centavos para reais
          const valorMatriculaReais = parseFloat(valorMatricula || '0') / 100;
          const valorTotalServicoReais = parseFloat(valorTotalServico || '0') / 100;
          
          // Aplicar desconto se existir
          let descontoPercentual = parseFloat(prev.desconto_percentual) || 0;
          let descontoValor = 0;
          
          // Se for indicação, usar desconto automático do cliente
          if (prev.eh_indicacao && prev.desconto_percentual) {
            descontoPercentual = parseFloat(prev.desconto_percentual);
          }
          
          // Calcular valor do desconto
          if (descontoPercentual > 0) {
            const totalAntesDesconto = valorTotalServicoReais;
            descontoValor = (totalAntesDesconto * descontoPercentual) / 100;
            newData.desconto_valor = Math.round(descontoValor * 100).toString(); // Converter para centavos
          } else {
            newData.desconto_valor = '0';
          }
          
          // Aplicar desconto e acréscimo ao total (base: valor da matrícula)
          const acrescimoValor = parseFloat(prev.acrescimo_valor ? (parseInt(prev.acrescimo_valor) / 100).toString() : '0') || 0;
          const totalFinal = valorMatriculaReais + acrescimoValor - descontoValor;
          const totalGeral = totalFinal;
          newData.valor_total_servico = Math.max(0, Math.round(totalFinal * 100)).toString();
          const valorParcela = parseFloat(parcelas) > 0 ? (totalGeral / parseFloat(parcelas)) : 0;
          
          // Converter de volta para centavos para armazenar (se houver parcelas)
          if (parseFloat(parcelas) > 0) {
            newData.valor_parcela = Math.round(valorParcela * 100).toString();
          }
          
          // Montar memória de cálculo
          let calcStr = `Memória de cálculo: (Valor da Matrícula: R$ ${formatarMoeda(valorMatriculaReais)} | Valor Total do Serviço: R$ ${formatarMoeda(valorTotalServicoReais)})`;
          
          if (descontoPercentual > 0) {
            calcStr += ` - Desconto ${descontoPercentual}%: R$ ${formatarMoeda(descontoValor)}`;
          }
          if (acrescimoValor > 0) {
            calcStr += ` + Acréscimo: R$ ${formatarMoeda(acrescimoValor)}`;
          }
          
          if (parseFloat(parcelas) > 0) {
            calcStr += ` = Total dos Serviços: R$ ${formatarMoeda(totalGeral)} ÷ Parcelas: ${parcelas} = Valor da Parcela: R$ ${formatarMoeda(valorParcela)}`;
          } else {
            calcStr += ` = Total dos Serviços: R$ ${formatarMoeda(totalGeral)}`;
          }
          
          // Remove linha anterior de memória de cálculo, se existir
          let obs = prev.observacoes || '';
          obs = obs.replace(/Memória de cálculo:.*(\n)?/, '');
          newData.observacoes = (obs ? obs + '\n' : '') + calcStr;
        }
      }
      
      // Se o campo eh_indicacao mudou, recarregar dados do cliente
      if (name === 'eh_indicacao' && checked) {
        if (prev.id_cliente) {
          carregarDadosCliente(prev.id_cliente);
        }
      }
      
      // Se o desconto percentual mudou, recalcular valores
      if (name === 'desconto_percentual') {
        const descontoPercentual = parseFloat(value) || 0;
        const valorMatricula = parseFloat(prev.valor_matricula) || 0;
        const valorTotalServico = parseFloat(prev.valor_total_servico) || 0;
        const parcelas = parseFloat(prev.parcelamento) || 0;

        if (valorTotalServico > 0) {
          const valorMatriculaReais = valorMatricula / 100;
          const valorTotalServicoReais = valorTotalServico / 100;
          const base = valorMatriculaReais;

          // Atualiza desconto_valor mesmo sem parcelas
          let descontoValor = 0;
          if (descontoPercentual > 0) {
            descontoValor = (base * descontoPercentual) / 100;
            newData.desconto_valor = Math.round(descontoValor * 100).toString();
          } else {
            newData.desconto_valor = '0';
          }

          // Só recalcula parcela/memória se parcelas > 0
          if (parcelas > 0) {
            const acrescimoValor2 = parseFloat(prev.acrescimo_valor ? (parseInt(prev.acrescimo_valor) / 100).toString() : '0') || 0;
            const totalGeral = valorMatriculaReais + acrescimoValor2 - descontoValor;
            newData.valor_total_servico = Math.max(0, Math.round(totalGeral * 100)).toString();
            const valorParcela = totalGeral / parcelas;
            newData.valor_parcela = Math.round(valorParcela * 100).toString();

            let calcStr = `Memória de cálculo: (Valor da Matrícula: R$ ${formatarMoeda(valorMatriculaReais)})`;
            if (descontoPercentual > 0) {
              calcStr += ` - Desconto ${descontoPercentual}%: R$ ${formatarMoeda(descontoValor)}`;
            }
            if (acrescimoValor2 > 0) {
              calcStr += ` + Acréscimo: R$ ${formatarMoeda(acrescimoValor2)}`;
            }
            calcStr += ` = Total: R$ ${formatarMoeda(totalGeral)} ÷ Parcelas: ${parcelas} = Valor da Parcela: R$ ${formatarMoeda(valorParcela)}`;

            let obs = prev.observacoes || '';
            obs = obs.replace(/Memória de cálculo:.*(\n)?/, '');
            newData.observacoes = (obs ? obs + '\n' : '') + calcStr;
          }
        }
      }

      // Se o acréscimo percentual mudou, recalcular valores
      if (name === 'acrescimo_percentual') {
        const acrescimoPercentual = parseFloat(value) || 0;
        const valorMatricula = parseFloat(prev.valor_matricula) || 0;
        const valorTotalServico = parseFloat(prev.valor_total_servico) || 0;
        const parcelas = parseFloat(prev.parcelamento) || 0;

        if (valorTotalServico > 0 || valorMatricula > 0) {
          const valorMatriculaReais = valorMatricula / 100;
          const valorTotalServicoReais = valorTotalServico / 100;
          const base = valorMatriculaReais;

          // Atualiza acrescimo_valor mesmo sem parcelas
          let acrescimoValor = 0;
          if (acrescimoPercentual > 0) {
            acrescimoValor = (base * acrescimoPercentual) / 100;
            newData.acrescimo_valor = Math.round(acrescimoValor * 100).toString();
          } else {
            newData.acrescimo_valor = '';
          }

          // Considerar desconto existente
          const descontoPercentual = parseFloat(prev.desconto_percentual) || 0;
          const descontoValor = descontoPercentual > 0 ? (base * descontoPercentual) / 100 : 0;

          if (parcelas > 0) {
            const totalGeral = valorMatriculaReais + acrescimoValor - descontoValor;
            newData.valor_total_servico = Math.max(0, Math.round(totalGeral * 100)).toString();
            const valorParcela = totalGeral / parcelas;
            newData.valor_parcela = Math.round(valorParcela * 100).toString();

            let calcStr = `Memória de cálculo: (Valor da Matrícula: R$ ${formatarMoeda(valorMatriculaReais)})`;
            if (descontoPercentual > 0) {
              calcStr += ` - Desconto ${descontoPercentual}%: R$ ${formatarMoeda(descontoValor)}`;
            }
            if (acrescimoPercentual > 0) {
              calcStr += ` + Acréscimo ${acrescimoPercentual}%: R$ ${formatarMoeda(acrescimoValor)}`;
            }
            calcStr += ` = Total: R$ ${formatarMoeda(totalGeral)} ÷ Parcelas: ${parcelas} = Valor da Parcela: R$ ${formatarMoeda(valorParcela)}`;

            let obs = prev.observacoes || '';
            obs = obs.replace(/Memória de cálculo:.*(\n)?/, '');
            newData.observacoes = (obs ? obs + '\n' : '') + calcStr;
          }
        }
      }
      
      return newData;
    });

    // Se o campo selecionado for a turma, carregar seus dados
    if (name === 'codigoTurma' && value) {
      carregarDadosTurma(value);
    }
  };

  const handleValorChange = (name, value) => {
    const numero = removerFormatacao(value);
    setFormData(prev => {
      const newData = { ...prev, [name]: numero };
      
      // Calcular valor da parcela automaticamente quando qualquer valor monetário ou parcelamento mudar
      if ((name === 'valor_total_servico' || name === 'parcelamento' || name === 'desconto_valor' || name === 'acrescimo_valor' || name === 'valor_matricula')) {
        const valorMatricula = name === 'valor_matricula' ? numero : prev.valor_matricula;
        const valorTotalServico = name === 'valor_total_servico' ? numero : prev.valor_total_servico;
        const parcelas = name === 'parcelamento' ? numero : prev.parcelamento;
        
        if (parcelas && parseFloat(parcelas) > 0) {
          // Converter de centavos para reais
          const valorMatriculaReais = parseFloat(valorMatricula || '0') / 100;
          const valorTotalServicoReais = parseFloat(valorTotalServico || '0') / 100;
          const baseCalculo = valorMatriculaReais;
          
          // Aplicar desconto/acréscimo se existir
          let descontoPercentual = parseFloat(prev.desconto_percentual) || 0;
          let descontoValor = 0;
          let acrescimoPercentual = parseFloat(prev.acrescimo_percentual) || 0;
          let acrescimoValor = parseFloat(prev.acrescimo_valor ? (parseInt(prev.acrescimo_valor) / 100).toString() : '0') || 0;
          
          // Se o campo alterado for o valor do desconto, usar o valor informado
          if (name === 'desconto_valor') {
            descontoValor = parseFloat(numero) / 100; // Converter de centavos para reais
            // Calcular o percentual baseado no valor informado
            if (baseCalculo > 0) {
              descontoPercentual = (descontoValor / baseCalculo) * 100;
              newData.desconto_percentual = descontoPercentual.toFixed(2);
            }
          } else {
            // Se for indicação, usar desconto automático do cliente
            if (prev.eh_indicacao && prev.desconto_percentual) {
              descontoPercentual = parseFloat(prev.desconto_percentual);
            }
            
            // Calcular valor do desconto
          if (descontoPercentual > 0) {
              descontoValor = (baseCalculo * descontoPercentual) / 100;
              newData.desconto_valor = Math.round(descontoValor * 100).toString(); // Converter para centavos
            } else {
              newData.desconto_valor = '0';
            }
          }

          // Sincronização do acréscimo
          if (name === 'acrescimo_valor') {
            const acrescimoReais = parseFloat(numero) / 100;
            if (baseCalculo > 0) {
              acrescimoPercentual = (acrescimoReais / baseCalculo) * 100;
              newData.acrescimo_percentual = acrescimoPercentual.toFixed(2);
              acrescimoValor = acrescimoReais;
            } else {
              newData.acrescimo_percentual = '';
              acrescimoValor = 0;
            }
          } else if (acrescimoPercentual > 0) {
            const acrescimoReais = (baseCalculo * acrescimoPercentual) / 100;
            newData.acrescimo_valor = Math.round(acrescimoReais * 100).toString();
            acrescimoValor = acrescimoReais;
          }
          
        // Aplicar desconto e acréscimo ao total (base: valor da matrícula)
        const totalGeral = valorMatriculaReais + acrescimoValor - descontoValor;
        newData.valor_total_servico = Math.max(0, Math.round(totalGeral * 100)).toString();
          const valorParcela = totalGeral / parseFloat(parcelas);
          
          // Converter de volta para centavos para armazenar
          newData.valor_parcela = Math.round(valorParcela * 100).toString();
          
          // Montar memória de cálculo
          let calcStr = `Memória de cálculo: (Valor da Matrícula: R$ ${formatarMoeda(valorMatriculaReais)})`;
          
          if (descontoPercentual > 0) {
            calcStr += ` - Desconto ${descontoPercentual}%: R$ ${formatarMoeda(descontoValor)}`;
          }
          if (acrescimoPercentual > 0 || acrescimoValor > 0) {
            const acrescimoLabel = acrescimoPercentual > 0 ? ` + Acréscimo ${acrescimoPercentual}%: R$ ${formatarMoeda(acrescimoValor)}` : ` + Acréscimo: R$ ${formatarMoeda(acrescimoValor)}`;
            calcStr += acrescimoLabel;
          }
          
          calcStr += ` = Total: R$ ${formatarMoeda(totalGeral)} ÷ Parcelas: ${parcelas} = Valor da Parcela: R$ ${formatarMoeda(valorParcela)}`;
          
          // Remove linha anterior de memória de cálculo, se existir
          let obs = prev.observacoes || '';
          obs = obs.replace(/Memória de cálculo:.*(\n)?/, '');
          newData.observacoes = (obs ? obs + '\n' : '') + calcStr;
        }
      }
      return newData;
    });
  };

  // Função para lidar com mudanças em campos de data
  const handleDataChange = (name, value) => {
    // Se o valor estiver vazio, limpa o campo
    if (!value) {
      setFormData(prev => ({ ...prev, [name]: '' }));
      return;
    }
    
    // Remove caracteres não numéricos
    const numeros = value.replace(/\D/g, '');
    
    // Se tiver mais de 8 dígitos, limita a 8
    if (numeros.length > 8) {
      return;
    }
    
    // Formata como DD/MM/AAAA
    let dataFormatada = '';
    if (numeros.length >= 1) dataFormatada += numeros.slice(0, 1);
    if (numeros.length >= 2) dataFormatada += numeros.slice(1, 2) + '/';
    if (numeros.length >= 3) dataFormatada += numeros.slice(2, 3);
    if (numeros.length >= 4) dataFormatada += numeros.slice(3, 4) + '/';
    if (numeros.length >= 5) dataFormatada += numeros.slice(4, 8);
    
    setFormData(prev => ({ ...prev, [name]: dataFormatada }));
  };

  // Função para converter data do formato DD/MM/AAAA para YYYY-MM-DD
  const formatarDataParaInput = (data) => {
    if (!data) return '';
    
    // Se já estiver no formato YYYY-MM-DD, retorna como está
    if (data.includes('-')) return data;
    
    // Remove caracteres não numéricos
    const numeros = data.replace(/\D/g, '');
    
    // Se tiver 8 dígitos, assume formato DDMMAAAA
    if (numeros.length === 8) {
      const dia = numeros.slice(0, 2);
      const mes = numeros.slice(2, 4);
      const ano = numeros.slice(4, 8);
      return `${ano}-${mes}-${dia}`;
    }
    
    // Se tiver 6 dígitos, assume formato DDMMAA
    if (numeros.length === 6) {
      const dia = numeros.slice(0, 2);
      const mes = numeros.slice(2, 4);
      const ano = '20' + numeros.slice(4, 6);
      return `${ano}-${mes}-${dia}`;
    }
    
    return data;
  };

  // Função para formatar data para exibição (DD/MM/AAAA)
  const formatarDataExibicao = (data) => {
    if (!data) return 'N/A';
    
    try {
      // Se já estiver no formato DD/MM/AAAA, retorna como está
      if (data.includes('/')) return data;
      
      // Se for um objeto Date (ISODate do MongoDB)
      if (data instanceof Date) {
        const dia = String(data.getDate()).padStart(2, '0');
        const mes = String(data.getMonth() + 1).padStart(2, '0');
        const ano = data.getFullYear();
        return `${dia}/${mes}/${ano}`;
      }
      
      // Se for uma string de data ISO (2025-07-08T00:00:00.000+0000)
      if (typeof data === 'string' && data.includes('T')) {
        const dataObj = new Date(data);
        if (!isNaN(dataObj.getTime())) {
          const dia = String(dataObj.getDate()).padStart(2, '0');
          const mes = String(dataObj.getMonth() + 1).padStart(2, '0');
          const ano = dataObj.getFullYear();
          return `${dia}/${mes}/${ano}`;
        }
      }
      
      // Se estiver no formato YYYY-MM-DD, converter para DD/MM/AAAA
      if (data.includes('-')) {
        const [ano, mes, dia] = data.split('-');
        return `${dia}/${mes}/${ano}`;
      }
      
      // Se for apenas números, tentar formatar
      const numeros = data.replace(/\D/g, '');
      if (numeros.length === 8) {
        const dia = numeros.slice(0, 2);
        const mes = numeros.slice(2, 4);
        const ano = numeros.slice(4, 8);
        return `${dia}/${mes}/${ano}`;
      }
      
      return data;
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return data;
    }
  };

  // Função para formatar dias da semana
  const formatarDiasSemana = (dias) => {
    if (!dias) return 'N/A';
    
    try {
      // Mapeamento dos dias da semana (texto)
      const mapeamentoDias = {
        'segunda': 'Seg',
        'terca': 'Ter', 
        'quarta': 'Qua',
        'quinta': 'Qui',
        'sexta': 'Sex',
        'sabado': 'Sab',
        'domingo': 'Dom',
        'Segunda': 'Seg',
        'Terca': 'Ter',
        'Quarta': 'Qua', 
        'Quinta': 'Qui',
        'Sexta': 'Sex',
        'Sabado': 'Sab',
        'Domingo': 'Dom',
        'SEGUNDA': 'Seg',
        'TERCA': 'Ter',
        'QUARTA': 'Qua',
        'QUINTA': 'Qui', 
        'SEXTA': 'Sex',
        'SABADO': 'Sab',
        'DOMINGO': 'Dom'
      };

      // Mapeamento dos dias da semana (numérico)
      const mapeamentoNumerico = {
        '0': 'Dom',
        '1': 'Seg', 
        '2': 'Ter',
        '3': 'Qua',
        '4': 'Qui',
        '5': 'Sex',
        '6': 'Sab',
        '7': 'Dom'
      };
      
      // Se for uma string com vírgulas, separar e formatar cada dia
      if (dias.includes(',')) {
        const diasArray = dias.split(',').map(dia => dia.trim());
        return diasArray.map(dia => {
          // Primeiro tenta mapeamento numérico
          if (mapeamentoNumerico[dia]) {
            return mapeamentoNumerico[dia];
          }
          // Depois tenta mapeamento textual
          return mapeamentoDias[dia] || dia;
        }).join(' - ');
      }
      
      // Se for uma string única
      // Primeiro tenta mapeamento numérico
      if (mapeamentoNumerico[dias]) {
        return mapeamentoNumerico[dias];
      }
      // Depois tenta mapeamento textual
      return mapeamentoDias[dias] || dias;
    } catch (error) {
      console.error('Erro ao formatar dias da semana:', error);
      return dias;
    }
  };

  // Função para obter nome do local
  const getNomeLocal = (localId) => {
    if (!localId) return 'N/A';
    
    try {
      // Se já for um nome (não um ID), retorna como está
      if (isNaN(localId)) return localId;
      
      // Buscar no array de locais
      const local = locais.find(l => l.id === parseInt(localId) || l._id === localId);
      return local ? (local.nome || local.descricao || localId) : localId;
    } catch (error) {
      console.error('Erro ao buscar nome do local:', error);
      return localId;
    }
  };

  // Função para obter nome do colaborador
  const getNomeColaborador = (colaboradorId) => {
    if (!colaboradorId) return 'N/A';
    
    try {
      // Se já for um nome (não um ID), retorna como está
      if (isNaN(colaboradorId)) return colaboradorId;
      
      // Buscar no array de colaboradores
      const colaborador = colaboradores.find(c => c.id === parseInt(colaboradorId) || c._id === colaboradorId);
      
      if (colaborador) {
        // Priorizar nome_completo, depois nome, depois ID
        return colaborador.nome_completo || colaborador.nome || colaboradorId;
      }
      
      // Se não encontrar, retorna o ID
      return colaboradorId;
    } catch (error) {
      console.error('Erro ao buscar nome do colaborador:', error);
      return colaboradorId;
    }
  };

  // Funções para verificar se campos devem estar habilitados (liberação gradativa)
  const isTabEnabled = (tabIndex) => {
    switch (tabIndex) {
      case 0: // Seleção - sempre habilitada
        return true;
      case 1: // Dados Pessoais - sempre habilitada (campos apenas leitura)
        return true;
      case 2: // Endereço - sempre habilitada (campos apenas leitura)
        return true;
      case 3: // Contato - sempre habilitada (campos apenas leitura)
        return true;
      case 4: // Dados da Turma - sempre habilitada (campos apenas leitura)
        return true;
      case 5: // Pagamento - habilitada se todos os dados anteriores preenchidos
        return !!(formData.id_cliente && formData.codigoTurma && formData.nome && 
                 formData.logradouro && formData.celular);
      default:
        return false;
    }
  };

  const isFieldEnabled = (fieldName) => {
    // Campos que sempre devem estar habilitados
    const alwaysEnabled = ['id_cliente', 'codigoTurma'];
    if (alwaysEnabled.includes(fieldName)) return true;

    // Campos que dependem de outros campos
    switch (fieldName) {
      // Dados Pessoais - sempre desabilitados (apenas leitura)
      case 'nome':
      case 'cpf_cnpj':
      case 'rg':
      case 'profissao':
      case 'pessoa_contato':
      case 'data_nascimento':
        return false;

      // Endereço - sempre desabilitados (apenas leitura)
      case 'logradouro':
      case 'numero':
      case 'complemento':
      case 'bairro':
      case 'cidade':
      case 'estado':
      case 'cep':
        return false;

      // Contato - sempre desabilitados (apenas leitura)
      case 'telefone_fixo':
      case 'celular':
      case 'email':
        return false;

      // Dados da Turma - sempre desabilitados (apenas leitura)
      case 'codigo_turma':
      case 'diaInicio':
      case 'diaFim':
      case 'duracao_minuto':
      case 'horaInicio':
      case 'horaFim':
      case 'diasDaSemana':
      case 'local':
      case 'colaborador':
        return false;

      // Pagamento - validação sequencial
      case 'forma_pagamento':
        return !!(formData.id_cliente && formData.codigoTurma && formData.nome && 
                 formData.logradouro && formData.celular);
      
      case 'parcelamento':
        return !!(formData.id_cliente && formData.codigoTurma && formData.nome && 
                 formData.logradouro && formData.celular && formData.forma_pagamento);
      
      case 'valor_matricula':
        return !!(formData.id_cliente && formData.codigoTurma && formData.nome && 
                 formData.logradouro && formData.celular && formData.forma_pagamento && 
                 formData.parcelamento);
      
      case 'valor_total_servico':
        return !!(formData.id_cliente && formData.codigoTurma && formData.nome && 
                 formData.logradouro && formData.celular && formData.forma_pagamento && 
                 formData.parcelamento && formData.valor_matricula);
      
      case 'desconto_percentual':
        return !!(formData.eh_indicacao && formData.id_cliente && formData.codigoTurma && formData.nome && 
                 formData.logradouro && formData.celular && formData.forma_pagamento && 
                 formData.parcelamento && formData.valor_matricula && formData.valor_total_servico);
      
      case 'desconto_valor':
        return !!(formData.eh_indicacao && formData.id_cliente && formData.codigoTurma && formData.nome && 
                 formData.logradouro && formData.celular && formData.forma_pagamento && 
                 formData.parcelamento && formData.valor_matricula && formData.valor_total_servico);
      
      case 'valor_parcela':
        return !!(formData.id_cliente && formData.codigoTurma && formData.nome && 
                 formData.logradouro && formData.celular && formData.forma_pagamento && 
                 formData.parcelamento && formData.valor_matricula && formData.valor_total_servico);
      
      case 'observacoes':
        return !!(formData.id_cliente && formData.codigoTurma && formData.nome && 
                 formData.logradouro && formData.celular && formData.forma_pagamento && 
                 formData.parcelamento && formData.valor_matricula && formData.valor_total_servico);

      default:
        return true;
    }
  };

  const getTabHelperText = (tabIndex) => {
    switch (tabIndex) {
      case 1: // Dados Pessoais
        return "Campos preenchidos automaticamente com os dados do cliente selecionado";
      case 2: // Endereço
        return "Campos preenchidos automaticamente com os dados do cliente selecionado";
      case 3: // Contato
        return "Campos preenchidos automaticamente com os dados do cliente selecionado";
      case 4: // Dados da Turma
        return "Campos preenchidos automaticamente com os dados da turma selecionada";
      case 5: // Pagamento
        if (!formData.id_cliente) return "Selecione um cliente primeiro";
        if (!formData.codigoTurma) return "Selecione uma turma primeiro";
        if (!formData.nome) return "Preencha os dados pessoais primeiro";
        if (!formData.logradouro) return "Preencha o endereço primeiro";
        if (!formData.celular) return "Preencha os dados de contato primeiro";
        return "";
      default:
        return "";
    }
  };

  // Função para obter mensagem de ajuda específica para campos da aba Pagamento
  const getPagamentoFieldHelperText = (fieldName) => {
    switch (fieldName) {
      case 'forma_pagamento':
        if (!formData.id_cliente) return "Selecione um cliente primeiro";
        if (!formData.codigoTurma) return "Selecione uma turma primeiro";
        if (!formData.nome) return "Preencha os dados pessoais primeiro";
        if (!formData.logradouro) return "Preencha o endereço primeiro";
        if (!formData.celular) return "Preencha os dados de contato primeiro";
        return "Selecione a forma de pagamento";
      
      case 'parcelamento':
        if (!formData.forma_pagamento) return "Selecione a forma de pagamento primeiro";
        return "Digite o número de parcelas";
      
      case 'valor_matricula':
        if (!formData.parcelamento) return "Digite o número de parcelas primeiro";
        return "Digite o valor da matrícula";
      
      case 'valor_total_servico':
        if (!formData.valor_matricula) return "Digite o valor da matrícula primeiro";
        return "Digite o valor total do serviço";
      
      case 'desconto_percentual':
        if (!formData.valor_total_servico) return "Digite o valor total do serviço primeiro";
        return "Digite a porcentagem de desconto (opcional)";
      
      case 'desconto_valor':
        if (!formData.valor_total_servico) return "Digite o valor total do serviço primeiro";
        return "Valor do desconto calculado automaticamente";
      
      case 'valor_parcela':
        if (!formData.valor_total_servico) return "Digite o valor total do serviço primeiro";
        return "Digite o valor de cada parcela";
      
      case 'observacoes':
        if (!formData.valor_total_servico) return "Digite o valor total do serviço primeiro";
        return "Campo opcional para observações importantes";
      
      default:
        return "";
    }
  };

  // Função para validar se todos os campos obrigatórios da aba Pagamento estão preenchidos
  const validarCamposPagamento = () => {
    const camposObrigatorios = [
      'forma_pagamento',
      'parcelamento', 
      'valor_matricula',
      'valor_total_servico',
      'valor_parcela'
    ];

    const camposVazios = camposObrigatorios.filter(campo => !formData[campo]);
    
    if (camposVazios.length > 0) {
      return false;
    }

    return true;
  };

  // Função para obter mensagem de erro dos campos da aba Pagamento
  const getMensagemErroPagamento = () => {
    const camposObrigatorios = [
      'forma_pagamento',
      'parcelamento', 
      'valor_matricula',
      'valor_total_servico',
      'valor_parcela'
    ];

    const camposVazios = camposObrigatorios.filter(campo => !formData[campo]);
    
    if (camposVazios.length > 0) {
      const primeiroCampoVazio = camposVazios[0];
      const mensagem = getPagamentoFieldHelperText(primeiroCampoVazio);
      return `Preencha o campo "${primeiroCampoVazio.replace('_', ' ').toUpperCase()}" primeiro: ${mensagem}`;
    }

    return null;
  };



  // Memoizar o progresso para evitar recálculos desnecessários
  const progressoPagamento = useMemo(() => {
    const camposObrigatorios = [
      'forma_pagamento',
      'parcelamento', 
      'valor_matricula',
      'valor_total_servico',
      'valor_parcela'
    ];

    const camposPreenchidos = camposObrigatorios.filter(campo => formData[campo]);
    const progresso = (camposPreenchidos.length / camposObrigatorios.length) * 100;
    
    return {
      progresso,
      camposPreenchidos: camposPreenchidos.length,
      totalCampos: camposObrigatorios.length,
      proximoCampo: camposObrigatorios.find(campo => !formData[campo])
    };
  }, [
    formData.forma_pagamento,
    formData.parcelamento,
    formData.valor_matricula,
    formData.valor_total_servico,
    formData.valor_parcela
  ]);

  // Função para carregar dados do cliente
  const carregarDadosCliente = (clienteId) => {
    
    const cliente = clientes.find(c => {
      // Verificar diferentes possibilidades de ID
      const matchById = c.id === parseInt(clienteId) || c._id === clienteId;
      const matchByString = c.id?.toString() === clienteId?.toString() || c._id?.toString() === clienteId?.toString();
      
      return matchById || matchByString;
      });
    
    if (cliente) {
      
      // Formatar data de nascimento se existir
      let dataNascimentoFormatada = '';
      if (cliente.data_nascimento) {
        try {
          // Se a data estiver no formato YYYY-MM-DD, converter para DD/MM/AAAA
          if (cliente.data_nascimento.includes('-')) {
            const [ano, mes, dia] = cliente.data_nascimento.split('-');
            dataNascimentoFormatada = `${dia}/${mes}/${ano}`;
          } else {
            // Se já estiver no formato DD/MM/AAAA, usar como está
            dataNascimentoFormatada = cliente.data_nascimento;
          }
        } catch (error) {
          console.error('Erro ao formatar data de nascimento:', error);
          dataNascimentoFormatada = cliente.data_nascimento || '';
        }
      }
      
      setFormData(prev => {
        const newData = {
          ...prev,
          nome: cliente.nome || cliente.nome_completo || '',
          data_nascimento: dataNascimentoFormatada,
          cpf_cnpj: cliente.cpf_cnpj || '',
          rg: cliente.rg || '',
          profissao: cliente.profissao || '',
          logradouro: cliente.logradouro || '',
          numero: cliente.numero || '',
          complemento: cliente.complemento || '',
          bairro: cliente.bairro || '',
          cidade: cliente.cidade || '',
          estado: cliente.estado || '',
          pessoa_contato: cliente.pessoa_contato || '',
          telefone_fixo: cliente.telefone_fixo || '',
          celular: cliente.celular || '',
          email: cliente.email || '',
          eh_indicacao: cliente.eh_indicacao || false,
          desconto_percentual: cliente.desconto_percentual || '',
          desconto_valor: cliente.desconto_valor || ''
        };

        // Se for indicação e tiver desconto, recalcular valores automaticamente
        if (cliente.eh_indicacao && cliente.desconto_percentual && 
            prev.valor_matricula && prev.valor_total_servico && prev.parcelamento) {
          
          const descontoPercentual = parseFloat(cliente.desconto_percentual) || 0;
          const valorMatricula = parseFloat(prev.valor_matricula) || 0;
          const valorTotalServico = parseFloat(prev.valor_total_servico) || 0;
          const parcelas = parseFloat(prev.parcelamento) || 0;
          
          if (valorMatricula > 0 && valorTotalServico > 0 && parcelas > 0) {
            // Converter de centavos para reais
            const valorMatriculaReais = valorMatricula / 100;
            const valorTotalServicoReais = valorTotalServico / 100;
            
            // Calcular valor do desconto
            let descontoValor = 0;
            if (descontoPercentual > 0) {
              const totalAntesDesconto = valorMatriculaReais + valorTotalServicoReais;
              descontoValor = (totalAntesDesconto * descontoPercentual) / 100;
              newData.desconto_valor = Math.round(descontoValor * 100).toString(); // Converter para centavos
            } else {
              newData.desconto_valor = '0';
            }
            
            // Recalcular valor da parcela
            const totalGeral = valorMatriculaReais + valorTotalServicoReais - descontoValor;
            const valorParcela = totalGeral / parcelas;
            newData.valor_parcela = Math.round(valorParcela * 100).toString();
            
            // Atualizar memória de cálculo
            let calcStr = `Memória de cálculo: (Valor da Matrícula: R$ ${valorMatriculaReais.toFixed(2)} + Valor Total do Serviço: R$ ${valorTotalServicoReais.toFixed(2)})`;
            
            if (descontoPercentual > 0) {
              calcStr += ` - Desconto ${descontoPercentual}%: R$ ${descontoValor.toFixed(2)}`;
            }
            
            calcStr += ` = Total: R$ ${totalGeral.toFixed(2)} ÷ Parcelas: ${parcelas} = Valor da Parcela: R$ ${valorParcela.toFixed(2)}`;
            
            // Remove linha anterior de memória de cálculo, se existir
            let obs = prev.observacoes || '';
            obs = obs.replace(/Memória de cálculo:.*(\n)?/, '');
            newData.observacoes = (obs ? obs + '\n' : '') + calcStr;
          }
        }

        return newData;
      });
      
    } else {
    }
  };

  // Função para carregar dados da turma
  const carregarDadosTurma = async (codigoTurma) => {
    
    const turma = turmas.find(t => {
      // Verificar diferentes possibilidades de código
      const matchByCodigo = t.codigoTurma === codigoTurma || t.codigo === codigoTurma;
      const matchByString = t.codigoTurma?.toString() === codigoTurma?.toString() || t.codigo?.toString() === codigoTurma?.toString();
      
      console.log("🔍 Verificando turma:", {
        codigoTurma: t.codigoTurma,
        codigo: t.codigo,
        matchByCodigo,
        matchByString,
        resultado: matchByCodigo || matchByString
      });

      return matchByCodigo || matchByString;
    });
    

    if (turma) {
      // Log de debug dos dados da turma
      console.log({
        codigoTurma: turma.codigoTurma,
        codigo: turma.codigo,
        servicoId: turma.servicoId,
        servico: turma.servico,
        nome: turma.nome
      });
      // Buscar o serviço associado à turma
      let valorServico = 0;
      let descricaoServico = '';
      if (turma.servicoId || turma.servico) {
        const servicoId = turma.servicoId || turma.servico;
        // Verificar se servicos é um array ou objeto paginado
        // Determinar o array de serviços (pode ser direto ou dentro de .dados)
        let arrayServicos = [];
        if (Array.isArray(servicos)) {
          arrayServicos = servicos;
        } else if (servicos && servicos.dados && Array.isArray(servicos.dados)) {
          arrayServicos = servicos.dados;
        }
        if (arrayServicos.length > 0) {
          const servico = arrayServicos.find(s => s.id === servicoId);
          if (servico) {
            // Log de debug dos dados do serviço
            console.log({
              id: servico.id,
              nome: servico.nome,
              precificacao: servico.precificacao,
              valorCliente: servico.precificacao?.valorCliente
            });
            if (servico.precificacao && servico.precificacao.valorCliente) {
              // Converter o valor para centavos (como os outros campos monetários)
              const valorOriginal = parseFloat(servico.precificacao.valorCliente);
              valorServico = Math.round(valorOriginal * 100);
              console.log({
                valorOriginal,
                valorEmCentavos: valorServico,
                valorFormatado: `R$ ${(valorServico / 100).toFixed(2)}`
              });
            }
            descricaoServico = servico.descricao || servico.nome || '';
          }
        }
        // Se não encontramos no array local, buscar diretamente pelo endpoint de serviço por ID
        if (!descricaoServico || valorServico === 0) {
          try {
            const token = localStorage.getItem("token");
            const headers = {
              'Authorization': `Bearer ${token}`,
              'x-second-key': env.SECOND_SECRET_KEY
            };
            const resServico = await api.get(`/servicos/${servicoId}`, { headers });
            const servicoResp = resServico.data?.servico || resServico.data;
            if (servicoResp) {
              if (servicoResp.precificacao && servicoResp.precificacao.valorCliente != null) {
                const valorOriginal = parseFloat(servicoResp.precificacao.valorCliente);
                if (!isNaN(valorOriginal)) {
                  valorServico = Math.round(valorOriginal * 100);
                }
              }
              descricaoServico = servicoResp.descricao || servicoResp.nome || descricaoServico;
            }
          } catch (e) {
            console.warn('Não foi possível buscar serviço por ID:', e?.response?.data || e?.message);
          }
        }
      }
      setFormData(prev => ({
        ...prev,
        codigo_turma: turma.codigoTurma || turma.codigo || '',
        diaInicio: turma.diaInicio || turma.dia_inicio || '',
        diaFim: turma.diaFim || turma.dia_fim || '',
        duracao_minuto: turma.duracao_minuto || turma.duracao || '',
        horaInicio: turma.horaInicio || turma.hora_inicio || '',
        horaFim: turma.horaFim || turma.hora_fim || '',
        diasDaSemana: turma.diasDaSemana || turma.dias_da_semana || '',
        local: turma.local || '',
        colaborador: turma.colaborador || '',
        valor_total_servico: valorServico.toString(), // Converter para string (como os outros campos monetários)
        valor_matricula: valorServico.toString(),
        descricao_servico: descricaoServico || ''
      }));
      // Log para debug das datas
      console.log({
        diaInicioOriginal: turma.diaInicio,
        diaFimOriginal: turma.diaFim,
        diaInicioFormatado: formatarDataExibicao(turma.diaInicio || turma.dia_inicio),
        diaFimFormatado: formatarDataExibicao(turma.diaFim || turma.dia_fim)
      });
    } else {
    }
    
  };

  // Função para imprimir o comprovante de matrícula
  const handleImprimirMatricula = (matricula) => {
    if (!matricula) {
      setError("Matrícula não encontrada");
      return;
    }

    // Obter dados do cliente
    const clienteId = matricula.id_cliente || formData.id_cliente;
    const cliente = clientes.find(c => c.id === parseInt(clienteId));
    const nomeCliente = cliente ? (cliente.nome || cliente.nome_completo) : (formData.nome || 'Não informado');
    const nomeResponsavel = cliente ? (cliente.pessoa_contato || nomeCliente) : (formData.pessoa_contato || nomeCliente);

    // Obter dados da turma
    const codTurma = matricula.codigoTurma || formData.codigoTurma || formData.codigo_turma;
    const turma = turmas.find(t => t.codigoTurma === codTurma);
    const nomeTurma = turma ? (turma.nome || turma.descricao) : (formData.descricao_servico || 'Não informado');
    const codigoTurma = codTurma || 'Não informado';

    // Formatar datas
    const dataInicio = (matricula.diaInicio || formData.diaInicio) ? formatarDataExibicao(matricula.diaInicio || formData.diaInicio) : 'Não informado';
    const dataFim = (matricula.diaFim || formData.diaFim) ? formatarDataExibicao(matricula.diaFim || formData.diaFim) : 'Não informado';
    const dataMatricula = matricula.data_matricula ? formatarDataExibicao(matricula.data_matricula) : new Date().toLocaleDateString('pt-BR');

    // Formatar valores
    const valorMatricula = (matricula.valor_matricula || formData.valor_matricula) ? formatarMoeda(matricula.valor_matricula || formData.valor_matricula) : 'R$ 0,00';
    const valorTotalServico = (matricula.valor_total_servico || formData.valor_total_servico) ? formatarMoeda(matricula.valor_total_servico || formData.valor_total_servico) : 'R$ 0,00';
    const valorParcela = (matricula.valor_parcela || formData.valor_parcela) ? formatarMoeda(matricula.valor_parcela || formData.valor_parcela) : 'R$ 0,00';
    const parcelamento = matricula.parcelamento || formData.parcelamento || '1';
    const formaPagamento = matricula.forma_pagamento || formData.forma_pagamento || 'Não informado';

    // Criar conteúdo do comprovante
    const conteudoComprovante = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Comprovante de Matrícula</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 15px;
            line-height: 1.4;
            color: #333;
            font-size: 12px;
          }
          .header {
            text-align: center;
            margin-bottom: 15px;
            border-bottom: 2px solid #333;
            padding-bottom: 10px;
          }
          .title {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 5px;
          }
          .subtitle {
            font-size: 11px;
            color: #666;
            margin-bottom: 10px;
          }
          .content {
            margin-bottom: 15px;
          }
          .field {
            margin-bottom: 8px;
            display: flex;
            align-items: baseline;
          }
          .label {
            font-weight: bold;
            min-width: 150px;
            flex-shrink: 0;
          }
          .value {
            border-bottom: 1px solid #ccc;
            flex-grow: 1;
            margin-left: 10px;
            padding-bottom: 2px;
          }
          .payment-info {
            background-color: #f9f9f9;
            padding: 10px;
            border-radius: 3px;
            margin: 10px 0;
            font-size: 11px;
          }
          .payment-info h4 {
            margin: 0 0 8px 0;
            font-size: 12px;
          }
          .lgpd-section {
            background-color: #e8f4f8;
            padding: 8px;
            border-radius: 3px;
            margin: 10px 0;
            font-size: 10px;
          }
          .lgpd-section h4 {
            margin: 0 0 5px 0;
            font-size: 11px;
          }
          .lgpd-section p {
            margin: 0;
            line-height: 1.3;
          }
          .signature-section {
            margin-top: 15px;
          }
          .signature-line {
            border-bottom: 1px solid #333;
            margin: 15px 0;
            padding: 5px 0;
          }
          .signature-line strong {
            font-size: 11px;
          }
          .two-columns {
            display: flex;
            gap: 20px;
          }
          .column {
            flex: 1;
          }
          @media print {
            body { 
              margin: 10px; 
              font-size: 11px;
            }
            .no-print { display: none; }
            .header { margin-bottom: 10px; }
            .content { margin-bottom: 10px; }
            .field { margin-bottom: 6px; }
            .payment-info { margin: 8px 0; padding: 8px; }
            .lgpd-section { margin: 8px 0; padding: 6px; }
            .signature-section { margin-top: 10px; }
            .signature-line { margin: 10px 0; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="title">COMPROVANTE DE MATRÍCULA</div>
          <div class="subtitle">(Documento emitido em conformidade com a Lei nº 13.709/2018 – LGPD)</div>
        </div>

        <div class="content">
          <p style="margin: 0 0 10px 0; font-size: 11px;">Declaramos, para os devidos fins, que o(a) aluno(a) abaixo identificado(a) está regularmente matriculado(a) nesta instituição de ensino:</p>

          <div class="two-columns">
            <div class="column">
              <div class="field">
                <span class="label">Nome do(a) Aluno(a):</span>
                <span class="value">${nomeCliente}</span>
              </div>

              <div class="field">
                <span class="label">Nome do Responsável:</span>
                <span class="value">${nomeResponsavel}</span>
              </div>

              <div class="field">
                <span class="label">Turma:</span>
                <span class="value">${nomeTurma}</span>
              </div>

              <div class="field">
                <span class="label">Código da Turma:</span>
                <span class="value">${codigoTurma}</span>
              </div>
            </div>

            <div class="column">
              <div class="field">
                <span class="label">Data de Início:</span>
                <span class="value">${dataInicio}</span>
              </div>

              <div class="field">
                <span class="label">Data de Término:</span>
                <span class="value">${dataFim}</span>
              </div>

              <div class="field">
                <span class="label">Data da Matrícula:</span>
                <span class="value">${dataMatricula}</span>
              </div>
            </div>
          </div>

          <div class="payment-info">
            <h4>Informações sobre o pagamento:</h4>
            <div class="two-columns">
              <div class="column">
                <div class="field">
                  <span class="label">Valor da Matrícula:</span>
                  <span class="value">${valorMatricula}</span>
                </div>
                <div class="field">
                  <span class="label">Valor Total do Serviço:</span>
                  <span class="value">${valorTotalServico}</span>
                </div>
              </div>
              <div class="column">
                <div class="field">
                  <span class="label">Forma de Pagamento:</span>
                  <span class="value">${formaPagamento}</span>
                </div>
                <div class="field">
                  <span class="label">Parcelamento:</span>
                  <span class="value">${parcelamento}x de ${valorParcela}</span>
                </div>
              </div>
            </div>
            ${formData.observacoes ? `
            <div class="field">
              <span class="label">Observações:</span>
              <span class="value">${formData.observacoes}</span>
            </div>
            ` : ''}
          </div>

          <p>A matrícula foi realizada conforme a documentação apresentada e em cumprimento aos critérios da instituição.</p>

          <div class="lgpd-section">
            <h4>Termo de Ciência – LGPD:</h4>
            <p>Os dados pessoais serão utilizados exclusivamente para fins educacionais, administrativos e legais, conforme LGPD (Lei nº 13.709/2018). O responsável declara estar ciente do tratamento dos dados pela instituição.</p>
          </div>
        </div>

        <div class="signature-section">
          <div class="two-columns">
            <div class="column">
              <div class="field">
                <span class="label">Local e Data:</span>
                <span class="value">${dataMatricula}</span>
              </div>
              <div class="signature-line">
                <strong>Assinatura do Responsável:</strong>
              </div>
            </div>
            <div class="column">
              <div class="signature-line">
                <strong>Assinatura e Carimbo da Instituição:</strong>
              </div>
            </div>
          </div>
        </div>

        <div class="no-print" style="text-align: center; margin-top: 30px;">
          <button onclick="window.print()">🖨️ Imprimir</button>
          <button onclick="window.close()">❌ Fechar</button>
        </div>
      </body>
      </html>
    `;

    // Abrir nova janela com o comprovante
    const novaJanela = window.open('', '_blank', 'width=800,height=600');
    novaJanela.document.write(conteudoComprovante);
    novaJanela.document.close();

    // Marcar que a matrícula foi impressa
    setMatriculaImpressa(true);
    setSuccess('Comprovante de matrícula gerado com sucesso! Imprima o documento antes de salvar.');
  };

  const handleCreate = async () => {
    // Validação específica para o campo cliente
    if (!formData.id_cliente) {
      setError("Por favor, selecione um cliente");
      return;
    }
    
    // Validação sequencial dos campos da aba Pagamento
    if (!validarCamposPagamento()) {
      const mensagemErro = getMensagemErroPagamento();
      if (mensagemErro) {
        setError(mensagemErro);
      }
      return;
    }
    
    // Validação geral dos campos obrigatórios
    if (!formData.codigoTurma) {
      setError("Por favor, selecione uma turma");
      return;
    }

    setLoadingForm(true);
    
    // Garantir que os serviços estejam carregados antes de prosseguir
    if (!servicos || (Array.isArray(servicos) && servicos.length === 0)) {
      await carregarServicos();
    }
    try {
      const token = localStorage.getItem("token");
      const codHashEmpresa = localStorage.getItem("st_codHashEmpresa");
      const headers = {
        'Authorization': `Bearer ${token}`,
        'x-second-key': env.SECOND_SECRET_KEY
      };

      // Normalizar forma de pagamento
      const normalizarFormaPagamento = (forma) => {
        if (!forma) return '';
        
        // Mapeamento dos valores do frontend para os valores válidos do banco
        const mapeamentoFormas = {
          'Pix': 'PIX',
          'Dinheiro': 'Dinheiro',
          'CartaoCredito': 'Cartão de Crédito',
          'CartaoDebito': 'Cartão de Débito',
          'Boleto': 'Boleto',
          'Transferencia': 'Transferência Bancária',
          'Cheque': 'Cheque',
          'Outros': 'Outros'
        };
        
        return mapeamentoFormas[forma] || 'PIX';
      };

      const dadosMatricula = {
        id_cliente: parseInt(formData.id_cliente),
        codigoTurma: formData.codigoTurma,
        forma_pagamento: normalizarFormaPagamento(formData.forma_pagamento),
        valor_matricula: parseFloat(formData.valor_matricula),
        valor_total_servico: parseFloat(formData.valor_total_servico),
        valor_servico: parseFloat(formData.valor_servico || formData.valor_total_servico),
        parcelamento: parseInt(formData.parcelamento),
        valor_parcela: parseFloat(formData.valor_parcela),
        observacoes: formData.observacoes,
        // dados do cliente
        data_nascimento: formData.data_nascimento,
        cpf_cnpj: formData.cpf_cnpj,
        rg: formData.rg,
        profissao: formData.profissao,
        logradouro: formData.logradouro,
        numero: formData.numero,
        complemento: formData.complemento,
        bairro: formData.bairro,
        cidade: formData.cidade,
        estado: formData.estado,
        pessoa_contato: formData.pessoa_contato,
        telefone_fixo: formData.telefone_fixo,
        celular: formData.celular,
        email: formData.email,
        eh_indicacao: !!formData.eh_indicacao,
        // descontos mantidos se necessários no backend
        desconto_percentual: formData.desconto_percentual,
        desconto_valor: formData.desconto_valor,
        // NÃO enviar acrescimo_percentual/acrescimo_valor
        // dados da turma
        codigo_turma: formData.codigo_turma,
        diaInicio: formData.diaInicio,
        diaFim: formData.diaFim,
        duracao_minuto: formData.duracao_minuto,
        horaInicio: formData.horaInicio,
        horaFim: formData.horaFim,
        diasDaSemana: formData.diasDaSemana,
        local: formData.local,
        colaborador: formData.colaborador,
        // empresa
        st_codHashEmpresa: codHashEmpresa
      };

      // LOG DETALHADO DO PAYLOAD ANTES DE ENVIAR
      const payloadDebug = {
        ...dadosMatricula
      };

      const responseMatricula = await api.post('/matricula', dadosMatricula, { headers });
      
      // Aguardar um pouco e garantir que os serviços estejam carregados
      if (!servicos || (Array.isArray(servicos) && servicos.length === 0)) {
        await new Promise(resolve => setTimeout(resolve, 500));
        await carregarServicos();
      }
      
      // Registrar parcelas no contas a receber
      try {
        const cliente = clientes.find(c => c.id === formData.id_cliente);
        
        const nomeCliente = cliente ? (cliente.nome || cliente.nome_completo) : 'Cliente';
        const cpfCliente = cliente ? cliente.cpf_cnpj : '';
        
        
        // Buscar categoria do serviço
        let categoriaServico = 'Serviços';
        
        if (turmas.length > 0) {
          const turma = turmas.find(t => t.codigoTurma === formData.codigoTurma);
          
          if (turma && turma.servicoId) {
            // Garantir que temos os serviços disponíveis
            let arrayServicos = [];
            
            // Primeiro, tentar usar o estado atual
            if (Array.isArray(servicos)) {
              arrayServicos = servicos;
            } else if (servicos && servicos.dados && Array.isArray(servicos.dados)) {
              arrayServicos = servicos.dados;
            }
            
            // Se não temos serviços no estado, carregar novamente
            if (arrayServicos.length === 0) {
              try {
                const token = localStorage.getItem("token");
                const codHashEmpresa = localStorage.getItem("st_codHashEmpresa");
                const headers = {
                  'Authorization': `Bearer ${token}`,
                  'x-second-key': env.SECOND_SECRET_KEY
                };
                
                const res = await api.get(`/servicos?st_codHashEmpresa=${codHashEmpresa}`, { headers });
                
                if (res.data && res.data.dados && Array.isArray(res.data.dados)) {
                  arrayServicos = res.data.dados;
                } else if (Array.isArray(res.data)) {
                  arrayServicos = res.data;
                } else {
                }
              } catch (err) {
                console.error('❌ Erro ao carregar serviços:', err);
                console.error('❌ Detalhes do erro:', err.response?.data);
              }
            }
            
            
            if (arrayServicos.length > 0) {
              const servico = arrayServicos.find(s => s.id === turma.servicoId);
              
              if (servico && servico.categoria) {
                categoriaServico = servico.categoria;
              } else {
              }
            } else {
            }
          } else {
          }
        } else {
        }
        
        // Calcular valor da parcela
        const valorTotalServico = parseFloat(formData.valor_total_servico) / 100; // Converter de centavos para reais
        const valorTotal = valorTotalServico;
        const valorParcela = valorTotal / parseInt(formData.parcelamento);
        

        // Data atual
        const dataAtual = new Date();
        
        // Criar parcelas
        
        for (let i = 0; i < parseInt(formData.parcelamento); i++) {
          // Calcular data de vencimento (1 mês para cada parcela)
          const dataVencimento = new Date(dataAtual);
          dataVencimento.setMonth(dataVencimento.getMonth() + i);
          
          const dadosParcela = {
            tipo: "receber",
            descricao: `Matrícula ${nomeCliente} - ${formData.codigoTurma}`,
            categoria: categoriaServico,
            id_pessoa: parseInt(formData.id_cliente),
            nome_pessoa: nomeCliente,
            documento_pessoa: cpfCliente || '',
            valor_total: parseFloat(valorParcela.toFixed(2)), // Garantir que seja número
            forma_pagamento: normalizarFormaPagamento(formData.forma_pagamento),
            data_emissao: dataAtual.toISOString().split('T')[0],
            data_vencimento: dataVencimento.toISOString().split('T')[0],
            status: "pendente",
            st_codHashEmpresa: codHashEmpresa
          };
          
          // Validar dados antes de enviar

          
          try {
            
            // Tentar com a URL completa primeiro
            let responseParcela;
            try {
              responseParcela = await api.post('/financeiro', dadosParcela, { headers });
            } catch (urlError) {
              responseParcela = await api.post('/financeiro', dadosParcela, { headers });
            }
            
          } catch (err) {
            console.error(`❌ Erro ao criar parcela ${i + 1}:`, err);
            console.error('❌ Detalhes do erro:', err.response?.data);
            console.error('❌ Status:', err.response?.status);
            console.error('❌ URL tentada:', '/financeiro');
            throw err; // Re-throw para ser capturado pelo catch externo
          }
        }
        
      } catch (err) {
        console.error("❌ Erro ao criar parcelas no contas a receber:", err);
        console.error("❌ Detalhes completos do erro:", {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status,
          statusText: err.response?.statusText
        });
        // Não interrompe o fluxo principal, apenas loga o erro
      }
      
      setSuccess("Matrícula criada com sucesso!");
      setOpenCreate(false);
      carregarMatriculas();
      
      // Log de auditoria removido - agora é feito no backend
      
    } catch (err) {
      console.error("Erro ao criar matrícula:", err);
      setError(err.response?.data?.message || "Erro ao criar matrícula");
    } finally {
      setLoadingForm(false);
    }
  };



  const handleDelete = async () => {
    if (!matriculaSelecionada) {
      console.error("❌ Matrícula selecionada é null/undefined");
      setError("Matrícula não encontrada para exclusão");
      return;
    }

    // Obter o ID da matrícula com diferentes possibilidades
    const matriculaId = matriculaSelecionada.id_matricula || matriculaSelecionada.id || matriculaSelecionada._id || matriculaSelecionada.matriculaId;
    
    if (!matriculaId) {
      console.error("❌ ID da matrícula não encontrado:", matriculaSelecionada);
      setError("ID da matrícula não encontrado");
      return;
    }

    // Log de debug dos dados da matrícula
    console.log({
      matriculaSelecionada,
      matriculaId,
      id_matricula: matriculaSelecionada.id_matricula,
      id: matriculaSelecionada.id,
      _id: matriculaSelecionada._id
    });

    setLoadingForm(true);
    try {
      const token = localStorage.getItem("token");
      const headers = {
        'Authorization': `Bearer ${token}`,
        'x-second-key': env.SECOND_SECRET_KEY
      };

      
      await api.delete(`/matricula/${matriculaId}`, { headers });
      
      setSuccess("Matrícula excluída com sucesso!");
      setOpenDelete(false);
      carregarMatriculas();
      
      // Log de auditoria removido - agora é feito no backend
      
    } catch (err) {
      console.error("❌ Erro completo:", err);
      console.error("❌ Status:", err.response?.status);
      console.error("❌ Data:", err.response?.data);
      console.error("❌ Headers:", err.response?.headers);
      console.error("❌ Erro ao excluir matrícula:", err.response?.data?.message || err.message);
      setError(err.response?.data?.message || "Erro ao excluir matrícula");
    } finally {
      setLoadingForm(false);
    }
  };

  // Filtro aplicado com validação por empresa
  const matriculasFiltradas = matriculas.filter(matricula => {
    // Validação por st_codHashEmpresa
    const st_codHashEmpresa = localStorage.getItem("st_codHashEmpresa");
    const usuarioLogado = JSON.parse(localStorage.getItem("usuario") || "{}");
    
    // Se não for superusuário, filtra por empresa
    if (usuarioLogado?.perfil !== "superusuario" && matricula.st_codHashEmpresa !== st_codHashEmpresa) {
      return false;
    }
    
    // Filtros de busca
    if (filtros.id_cliente && matricula.id_cliente.toString() !== filtros.id_cliente) return false;
    if (filtros.codigoTurma && matricula.codigoTurma !== filtros.codigoTurma) return false;
    return true;
  });

  // Ordenação
  const matriculasOrdenadas = matriculasFiltradas.sort((a, b) => {
    const isAsc = order === "asc";
    
    if (orderBy === "cliente") {
      const nomeA = getNomeCliente(a.id_cliente).toLowerCase();
      const nomeB = getNomeCliente(b.id_cliente).toLowerCase();
      return isAsc ? nomeA.localeCompare(nomeB) : nomeB.localeCompare(nomeA);
    }
    
    if (orderBy === "status_cliente") {
      const statusA = getStatusCliente(a.id_cliente).toLowerCase();
      const statusB = getStatusCliente(b.id_cliente).toLowerCase();
      return isAsc ? statusA.localeCompare(statusB) : statusB.localeCompare(statusA);
    }
    
    if (orderBy === "turma") {
      const nomeA = getNomeTurma(a.codigoTurma).toLowerCase();
      const nomeB = getNomeTurma(b.codigoTurma).toLowerCase();
      return isAsc ? nomeA.localeCompare(nomeB) : nomeB.localeCompare(nomeA);
    }
    
    if (orderBy === "descricao_turma") {
      const descA = getDescricaoTurma(a.codigoTurma).toLowerCase();
      const descB = getDescricaoTurma(b.codigoTurma).toLowerCase();
      return isAsc ? descA.localeCompare(descB) : descB.localeCompare(descA);
    }
    
    if (orderBy === "local") {
      const localA = getLocalTurma(a.codigoTurma).toLowerCase();
      const localB = getLocalTurma(b.codigoTurma).toLowerCase();
      return isAsc ? localA.localeCompare(localB) : localB.localeCompare(localA);
    }
    
    if (orderBy === "periodo") {
      const periodoA = getPeriodoTurma(a.codigoTurma);
      const periodoB = getPeriodoTurma(b.codigoTurma);
      if (periodoA === 'N/A' && periodoB === 'N/A') return 0;
      if (periodoA === 'N/A') return isAsc ? 1 : -1;
      if (periodoB === 'N/A') return isAsc ? -1 : 1;
      return isAsc ? periodoA.localeCompare(periodoB) : periodoB.localeCompare(periodoA);
    }
    
    if (orderBy === "valor_matricula" || orderBy === "valor_total") {
      const valA = parseFloat(a[orderBy] || 0);
      const valB = parseFloat(b[orderBy] || 0);
      return isAsc ? valA - valB : valB - valA;
    }
    
    // Ordenação padrão por ID
    const valA = a[orderBy] || 0;
    const valB = b[orderBy] || 0;
    return isAsc ? valA - valB : valB - valA;
  });

  const matriculasPaginadas = matriculasOrdenadas.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <AcessoRestrito modulo="matricula">
      <Box
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Snackbar 
          open={!!error} 
          autoHideDuration={6000} 
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>

        <Snackbar 
          open={!!success} 
          autoHideDuration={6000} 
          onClose={() => setSuccess(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
            {success}
          </Alert>
        </Snackbar>

        {/* Box interno com margens de 1,5% */}
        <Box sx={{
          margin: '0 1.5% 1.5% 1.5%',  // 1,5% nas laterais e inferior
          height: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Box sx={{ 
            p: { xs: 2, sm: 3 }, 
            pb: 2,
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 }
          }}>
            <Typography 
              variant="h5" 
              mb={1} 
              sx={{ 
                fontSize: { xs: '1.5rem', sm: '2rem' },
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <SchoolIcon sx={{ 
                fontSize: { xs: '1.5rem', sm: '2rem' },
                color: '#FFC107' // Mesma cor usada no menu lateral
              }} />
              Matrículas
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button 
                variant="contained" 
                onClick={handleAbrirCreate}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  minWidth: { xs: 'auto', sm: '200px' },
                  backgroundColor: (theme) => theme.palette.mode === 'dark'
                    ? '#ffffff'
                    : theme.palette.primary.main,
                  color: (theme) => theme.palette.mode === 'dark'
                    ? '#000000'
                    : theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.mode === 'dark'
                      ? '#f5f5f5'
                      : theme.palette.primary.dark,
                  }
                }}
              >
                <AddIcon /> Nova Matrícula
              </Button>
            </Box>
          </Box>

          {/* Container da Tabela */}
          <TableContainer 
            component={Paper} 
            sx={{ 
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              overflow: 'auto',
              height: {
                xs: 'calc(90vh - 140px)',  // Mobile: mais espaço para header
                sm: 'calc(90vh - 120px)',  // Tablet: espaço padrão
                md: 'calc(90vh - 120px)',  // Desktop: espaço padrão
                lg: 'calc(90vh - 120px)'   // Large: espaço padrão
              },
              maxHeight: {
                xs: 'calc(90vh - 140px)',
                sm: 'calc(90vh - 120px)',
                md: 'calc(90vh - 120px)',
                lg: 'calc(90vh - 120px)'
              },
              width: '100%', // ✅ 100% da largura da página
              minWidth: { xs: '100%', sm: '600px' }, // ✅ 100% do mínimo também
              maxWidth: '100%', // ✅ Limita a 100% máximo
              boxShadow: (theme) => theme.palette.mode === 'dark' 
                ? '0 2px 8px rgba(0, 0, 0, 0.15)' 
                : '0 2px 8px rgba(0, 0, 0, 0.06)',
              borderRadius: 2,
              border: 'none'
            }}
          >
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                zIndex: 1
              }}
            >
              <CircularProgress />
            </Box>
          )}
          
          <Table sx={{ minWidth: '100%', tableLayout: 'fixed' }}>
            <TableHead sx={{
              position: 'sticky',
              top: 0,
              zIndex: 10,
              backgroundColor: (theme) => theme.palette.background.paper,
              '& .MuiTableCell-root': {
                backgroundColor: 'inherit',
                borderBottom: '1px solid rgba(224, 224, 224, 1)'
              }
            }}>
              <TableRow>
               <TableCell sx={{ 
                 width: { xs: '15%', sm: '15%' },
                 minWidth: { xs: '140px', sm: '110px' }
               }}>
                  <TableSortLabel
                    active={orderBy === "cliente"}
                    direction={orderBy === "cliente" ? order : "asc"}
                    onClick={() => handleRequestSort("cliente")}
                  >
                    Cliente
                  </TableSortLabel>
                  <TextField
                    name="id_cliente"
                    size="small"
                    fullWidth
                    value={filtros.id_cliente}
                    onChange={handleFiltroChange}
                    placeholder="Filtrar por cliente"
                  />
                </TableCell>
               <TableCell sx={{ 
                 width: { xs: '9%', sm: '9%' },
                 minWidth: { xs: '90px', sm: '70px' }
               }}>
                  <TableSortLabel
                    active={orderBy === "status_cliente"}
                    direction={orderBy === "status_cliente" ? order : "asc"}
                    onClick={() => handleRequestSort("status_cliente")}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
               <TableCell sx={{ 
                 width: { xs: '13%', sm: '13%' },
                 minWidth: { xs: '110px', sm: '90px' }
               }}>
                  <TableSortLabel
                    active={orderBy === "turma"}
                    direction={orderBy === "turma" ? order : "asc"}
                    onClick={() => handleRequestSort("turma")}
                  >
                    Cód. Turma
                  </TableSortLabel>
                  <TextField
                    name="codigoTurma"
                    size="small"
                    fullWidth
                    value={filtros.codigoTurma}
                    onChange={handleFiltroChange}
                    placeholder="Filtrar por turma"
                  />
                </TableCell>
               <TableCell sx={{ 
                 width: { xs: '11%', sm: '11%' },
                 minWidth: { xs: '110px', sm: '90px' }
               }}>
                  <TableSortLabel
                    active={orderBy === "descricao_turma"}
                    direction={orderBy === "descricao_turma" ? order : "asc"}
                    onClick={() => handleRequestSort("descricao_turma")}
                  >
                    Descrição Turma
                  </TableSortLabel>
                </TableCell>
               <TableCell sx={{ 
                 width: { xs: '12%', sm: '12%' },
                 minWidth: { xs: '90px', sm: '70px' }
               }}>
                  <TableSortLabel
                    active={orderBy === "local"}
                    direction={orderBy === "local" ? order : "asc"}
                    onClick={() => handleRequestSort("local")}
                  >
                    Local
                  </TableSortLabel>
                </TableCell>
               <TableCell sx={{ 
                 width: { xs: '12%', sm: '12%' },
                 minWidth: { xs: '110px', sm: '90px' }
               }}>
                  <TableSortLabel
                    active={orderBy === "periodo"}
                    direction={orderBy === "periodo" ? order : "asc"}
                    onClick={() => handleRequestSort("periodo")}
                  >
                    Período
                  </TableSortLabel>
                </TableCell>
               <TableCell sx={{ 
                 width: { xs: '6%', sm: '6%' },
                 minWidth: { xs: '70px', sm: '55px' }
               }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {matriculasPaginadas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body1" color="text.secondary">
                      {matriculas.length === 0 
                        ? "Nenhuma matrícula encontrada. Clique em 'Nova Matrícula' para começar."
                        : "Nenhuma matrícula corresponde aos filtros aplicados."
                      }
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                matriculasPaginadas.map((matricula, index) => (
                  <TableRow 
                    key={matricula.id_matricula || matricula.id || matricula._id || `matricula-${matricula.codigoTurma}-${matricula.id_cliente}`}
                    sx={{
                      backgroundColor: (theme) => 
                        index % 2 === 0 
                          ? theme.palette.mode === 'dark' 
                            ? 'rgba(255, 255, 255, 0.02)' 
                            : 'rgba(0, 0, 0, 0.02)'
                          : 'transparent',
                      '&:hover': {
                        backgroundColor: (theme) => theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.08)' 
                          : 'rgba(0, 0, 0, 0.08)',
                      }
                    }}
                  >
                    <TableCell sx={{ width: { xs: '15%', sm: '15%' }, minWidth: { xs: '140px', sm: '110px' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.2 }}>
                      <Typography variant="body2">
                        {getNomeCliente(matricula.id_cliente)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ width: { xs: '9%', sm: '9%' }, minWidth: { xs: '90px', sm: '70px' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.2 }}>
                      <Box sx={getStatusStyles(getStatusCliente(matricula.id_cliente))}>
                        {getStatusCliente(matricula.id_cliente)}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ width: { xs: '13%', sm: '13%' }, minWidth: { xs: '110px', sm: '90px' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.2 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {matricula.codigoTurma}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ width: { xs: '11%', sm: '11%' }, minWidth: { xs: '110px', sm: '90px' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.2 }}>
                      <Typography variant="body2">
                        {getDescricaoTurma(matricula.codigoTurma)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ width: { xs: '12%', sm: '12%' }, minWidth: { xs: '90px', sm: '70px' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.2 }}>
                      <Typography variant="body2">
                        {getLocalTurma(matricula.codigoTurma)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ width: { xs: '12%', sm: '12%' }, minWidth: { xs: '110px', sm: '90px' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.2 }}>
                      <Typography variant="body2">
                        {getPeriodoTurma(matricula.codigoTurma)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ width: { xs: '6%', sm: '6%' }, minWidth: { xs: '70px', sm: '55px' } }}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Visualizar">
                          <IconButton 
                            size="small" 
                            onClick={() => handleAbrirView(matricula)}
                            sx={{ color: 'info.main' }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Imprimir Matrícula">
                          <IconButton 
                            size="small" 
                            onClick={() => handleImprimirMatricula(matricula)}
                            sx={{ color: 'primary.main' }}
                          >
                            <PrintIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Excluir">
                          <IconButton 
                            size="small" 
                            onClick={() => handleAbrirDelete(matricula)}
                            sx={{ color: 'error.main' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

          {/* Paginação fora do TableContainer */}
          <TablePagination
            component="div"
            count={matriculasFiltradas.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
            labelRowsPerPage="Itens por página"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={{
              backgroundColor: (theme) => theme.palette.background.default,
              borderTop: 'none',
              width: '100%',
              marginTop: 0
            }}
          />
        </Box> {/* Fechamento do Box interno com margens */}

        {/* Dialog de Criação */}
        <Dialog
          open={openCreate}
          onClose={() => setOpenCreate(false)}
          maxWidth="md"
          fullWidth
          disableEscapeKeyDown
          disableBackdropClick
          disablePortal
          hideBackdrop={false}
          sx={{
            '& .MuiDialog-backdrop': {
              backgroundColor: 'rgba(0, 0, 0, 0.8)'
            }
          }}
          PaperProps={{
            sx: {
              width: '1296px',
              height: '696px',
              position: 'relative'
            }
          }}
        >
          <DialogTitle sx={{ 
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            borderBottom: 1,
            borderColor: 'divider',
            padding: 2,
            position: 'sticky',
            top: 0,
            zIndex: 1,
            fontSize: '1.25rem',
            fontWeight: 500
          }}>
            Nova Matrícula
            <IconButton
              onClick={() => setOpenCreate(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
                        <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ 
            height: 'calc(100% - 140px)',
            overflow: 'auto',
            padding: 2,
            '&.MuiDialogContent-root': {
              paddingTop: 2
            },
            position: 'relative',
            top: 0,
            marginBottom: '60px',
            '& .MuiInputLabel-root': {
              fontSize: '0.875rem'
            },
            '& .MuiInputBase-input': {
              fontSize: '0.875rem'
            },
            '& .MuiFormControlLabel-label': {
              fontSize: '0.875rem'
            }
          }}>
                          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={tabValue} onChange={handleTabChange} aria-label="formulário de matrícula">
                  <Tab label="Seleção" />
                  <Tab label="Dados Pessoais" />
                  <Tab label="Endereço" />
                  <Tab label="Contato" />
                  <Tab label="Dados da Turma" />
                  <Tab label="Pagamento" />
                </Tabs>
              </Box>

            {/* Tab 1: Seleção */}
            {tabValue === 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box>
                  <Autocomplete
                    options={clientesOrdenados}
                    getOptionLabel={(option) => option.nome_completo || option.nome || ''}
                    value={clientesOrdenados.find(cliente => cliente.id === formData.id_cliente || cliente._id === formData.id_cliente) || null}
                    onChange={(event, newValue) => {
                      if (newValue) {
                        const clienteId = newValue.id || newValue._id;
                        
                        // Validar status do cliente antes de permitir a seleção
                        if (!validarStatusCliente(clienteId)) {
                          // Se a validação falhou, não selecionar o cliente
                          return;
                        }
                        
                        setFormData(prev => ({ ...prev, id_cliente: clienteId }));
                        carregarDadosCliente(clienteId);
                      } else {
                        setFormData(prev => ({ ...prev, id_cliente: '' }));
                        // Limpar dados do cliente quando nenhum for selecionado
                        setFormData(prev => ({
                            ...prev,
                            nome: '',
                            data_nascimento: '',
                            cpf_cnpj: '',
                            rg: '',
                            profissao: '',
                            logradouro: '',
                            numero: '',
                            complemento: '',
                            bairro: '',
                            cidade: '',
                            estado: '',
                            pessoa_contato: '',
                            telefone_fixo: '',
                            celular: '',
                            email: ''
                        }));
                      }
                    }}
                    filterOptions={(options, { inputValue }) => {
                      const filterValue = inputValue.toLowerCase();
                      return options.filter(option => {
                        const nome = (option.nome_completo || option.nome || '').toLowerCase();
                        const cpf = (option.cpf_cnpj || '').toLowerCase();
                        return nome.includes(filterValue) || cpf.includes(filterValue);
                      });
                    }}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body1">
                            {option.nome_completo || option.nome}
                          </Typography>
                          {option.cpf_cnpj && (
                            <Typography variant="caption" color="text.secondary">
                              {formatarCPFCNPJLGPD(option.cpf_cnpj, option.tipo_pessoa, true)}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Cliente *"
                        placeholder="Digite para buscar..."
                        required
                        error={!formData.id_cliente}
                        helperText={!formData.id_cliente ? "Cliente é obrigatório" : ""}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingClientes ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    loading={loadingClientes}
                    noOptionsText={loadingClientes ? "Carregando clientes..." : "Nenhum cliente encontrado"}
                    disabled={loadingClientes}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: '0.875rem'
                      }
                    }}
                  />
                  {!loadingClientes && clientesOrdenados.length === 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                        Nenhum cliente encontrado. Verifique sua conexão.
                      </Typography>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        onClick={carregarClientes}
                        disabled={loadingClientes}
                      >
                        {loadingClientes ? <CircularProgress size={16} /> : 'Tentar Novamente'}
                      </Button>
                    </Box>
                  )}
                </Box>

                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: '1 1 300px' }}>
                      <FormControl fullWidth>
                        <InputLabel>Turma *</InputLabel>
                        <Select
                          name="codigoTurma"
                          value={formData.codigoTurma}
                          onChange={handleFormChange}
                          label="Turma *"
                          required
                          error={!formData.codigoTurma}
                        >
                          <MenuItem value="">
                            <em>Selecione uma turma...</em>
                          </MenuItem>
                        {turmas.map((turma) => (
                          <MenuItem key={turma.codigoTurma} value={turma.codigoTurma}>
                            {turma.nome || turma.codigoTurma}
                              </MenuItem>
                        ))}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box sx={{ flex: '1 1 300px' }}>
                      <TextField
                        fullWidth
                        name="codigo_turma"
                        label="Código da Turma"
                        value={formData.codigo_turma}
                        onChange={handleFormChange}
                        disabled
                        helperText="Código interno da turma"
                      />
                    </Box>
                    </Box>
              </Box>
            )}

            {/* Tab 2: Dados Pessoais */}
            {tabValue === 1 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {!isTabEnabled(1) && (
                  <Box sx={{ 
                    backgroundColor: 'warning.light', 
                    color: 'warning.contrastText', 
                    padding: 2, 
                    borderRadius: 1, 
                    mb: 2 
                  }}>
                    <Typography variant="body2">
                      {getTabHelperText(1)}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <TextField
                    fullWidth
                    name="nome"
                    label="Nome Completo"
                    value={formData.nome}
                    onChange={handleFormChange}
                    disabled={!isFieldEnabled('nome')}
                    helperText={!isFieldEnabled('nome') ? getTabHelperText(1) : "Preenchido automaticamente ao selecionar o cliente"}
                  />
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 200px' }}>
                    <TextField
                      fullWidth
                      name="cpf_cnpj"
                      label="CPF/CNPJ"
                      value={formData.cpf_cnpj ? formatarCPFCNPJLGPD(formData.cpf_cnpj, formData.tipo_pessoa, true) : ''}
                      onChange={handleFormChange}
                      disabled={!isFieldEnabled('cpf_cnpj')}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 200px' }}>
                    <TextField
                      fullWidth
                      name="rg"
                      label="RG"
                      value={formData.rg}
                      onChange={handleFormChange}
                      disabled={!isFieldEnabled('rg')}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 200px' }}>
                    <TextField
                      fullWidth
                      name="data_nascimento"
                      label="Data de Nascimento"
                      value={formData.data_nascimento}
                      onChange={(e) => handleDataChange("data_nascimento", e.target.value)}
                      placeholder="DD/MM/AAAA"
                      inputProps={{
                        maxLength: 10
                      }}
                    />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      name="profissao"
                      label="Profissão"
                      value={formData.profissao}
                      onChange={handleFormChange}
                      disabled={!isFieldEnabled('profissao')}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      name="pessoa_contato"
                      label="Pessoa para Contato"
                      value={formData.pessoa_contato}
                      onChange={handleFormChange}
                      disabled={!isFieldEnabled('pessoa_contato')}
                    />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 200px' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.eh_indicacao}
                          onChange={handleFormChange}
                          name="eh_indicacao"
                          disabled
                        />
                      }
                      label="É Indicação"
                    />
                  </Box>
                  {formData.eh_indicacao && (
                    <>
                      <Box sx={{ flex: '1 1 200px' }}>
                        <TextField
                          fullWidth
                          name="desconto_percentual"
                          label="Desconto (%)"
                          value={formData.desconto_percentual}
                          onChange={handleFormChange}
                          type="number"
                          inputProps={{ min: 0, max: 100, step: 0.01 }}
                          disabled
                        />
                      </Box>
                      <Box sx={{ flex: '1 1 200px' }}>
                        <TextField
                          fullWidth
                          name="desconto_valor"
                          label="Desconto (R$)"
                          value={formData.desconto_valor}
                          onChange={handleFormChange}
                          type="number"
                          inputProps={{ min: 0, step: 0.01 }}
                          disabled
                        />
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            )}

            {/* Tab 3: Endereço */}
            {tabValue === 2 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {!isTabEnabled(2) && (
                  <Box sx={{ 
                    backgroundColor: 'warning.light', 
                    color: 'warning.contrastText', 
                    padding: 2, 
                    borderRadius: 1, 
                    mb: 2 
                  }}>
                    <Typography variant="body2">
                      {getTabHelperText(2)}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 400px' }}>
                    <TextField
                      fullWidth
                      name="logradouro"
                      label="Logradouro"
                      value={formData.logradouro}
                      onChange={handleFormChange}
                      disabled={!isFieldEnabled('logradouro')}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 150px' }}>
                    <TextField
                      fullWidth
                      name="numero"
                      label="Número"
                      value={formData.numero}
                      onChange={handleFormChange}
                      disabled={!isFieldEnabled('numero')}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 150px' }}>
                    <TextField
                      fullWidth
                      name="complemento"
                      label="Complemento"
                      value={formData.complemento}
                      onChange={handleFormChange}
                      disabled={!isFieldEnabled('complemento')}
                    />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      name="bairro"
                      label="Bairro"
                      value={formData.bairro}
                      onChange={handleFormChange}
                      disabled={!isFieldEnabled('bairro')}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      name="cidade"
                      label="Cidade"
                      value={formData.cidade}
                      onChange={handleFormChange}
                      disabled={!isFieldEnabled('cidade')}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 200px' }}>
                    <TextField
                      fullWidth
                      name="estado"
                      label="Estado"
                      value={formData.estado}
                      onChange={handleFormChange}
                      disabled={!isFieldEnabled('estado')}
                    />
                  </Box>
                </Box>
              </Box>
            )}

            {/* Tab 4: Contato */}
            {tabValue === 3 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {!isTabEnabled(3) && (
                  <Box sx={{ 
                    backgroundColor: 'warning.light', 
                    color: 'warning.contrastText', 
                    padding: 2, 
                    borderRadius: 1, 
                    mb: 2 
                  }}>
                    <Typography variant="body2">
                      {getTabHelperText(3)}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <TextField
                    fullWidth
                    name="email"
                    label="E-mail"
                    value={formData.email ? formatarEmailLGPD(formData.email, true) : ''}
                    onChange={handleFormChange}
                    disabled={!isFieldEnabled('email')}
                  />
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      name="celular"
                      label="Celular"
                      value={formData.celular ? formatarTelefoneLGPD(formData.celular, true) : ''}
                      onChange={handleFormChange}
                      disabled={!isFieldEnabled('celular')}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      name="telefone_fixo"
                      label="Telefone Fixo"
                      value={formData.telefone_fixo ? formatarTelefoneLGPD(formData.telefone_fixo, true) : ''}
                      onChange={handleFormChange}
                      disabled={!isFieldEnabled('telefone_fixo')}
                    />
                  </Box>
                </Box>
              </Box>
            )}

            {/* Tab 5: Dados da Turma */}
            {tabValue === 4 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {formData.codigoTurma ? (
                  <>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ flex: '1 1 200px' }}>
                        <TextField
                          fullWidth
                          name="diaInicio"
                          label="Data de Início"
                          value={formatarDataExibicao(formData.diaInicio)}
                          onChange={handleFormChange}
                          disabled
                        />
                      </Box>
                      <Box sx={{ flex: '1 1 200px' }}>
                        <TextField
                          fullWidth
                          name="diaFim"
                          label="Data de Término"
                          value={formatarDataExibicao(formData.diaFim)}
                          onChange={handleFormChange}
                          disabled
                        />
                      </Box>
                      <Box sx={{ flex: '1 1 200px' }}>
                        <TextField
                          fullWidth
                          name="duracao_minuto"
                          label="Duração (minutos)"
                          value={formData.duracao_minuto}
                          onChange={handleFormChange}
                          disabled
                        />
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ flex: '1 1 200px' }}>
                        <TextField
                          fullWidth
                          name="horaInicio"
                          label="Hora de Início"
                          value={formData.horaInicio}
                          onChange={handleFormChange}
                          disabled
                        />
                      </Box>
                      <Box sx={{ flex: '1 1 200px' }}>
                        <TextField
                          fullWidth
                          name="horaFim"
                          label="Hora de Término"
                          value={formData.horaFim}
                          onChange={handleFormChange}
                          disabled
                        />
                      </Box>
                      <Box sx={{ flex: '1 1 200px' }}>
                        <TextField
                          fullWidth
                          name="diasDaSemana"
                          label="Dias da Semana"
                          value={formatarDiasSemana(formData.diasDaSemana)}
                          onChange={handleFormChange}
                          disabled
                        />
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ flex: '1 1 300px' }}>
                        <TextField
                          fullWidth
                          name="local"
                          label="Local"
                          value={getNomeLocal(formData.local)}
                          onChange={handleFormChange}
                          disabled
                        />
                      </Box>
                      <Box sx={{ flex: '1 1 300px' }}>
                        <TextField
                          fullWidth
                          name="colaborador"
                          label="Colaborador Responsável"
                          value={getNomeColaborador(formData.colaborador)}
                          onChange={handleFormChange}
                          disabled
                        />
                      </Box>
                    </Box>
                  </>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      Selecione uma turma na aba "Seleção" para ver os detalhes
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Tab 6: Pagamento */}
            {tabValue === 5 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {!isTabEnabled(5) && (
                  <Box sx={{ 
                    backgroundColor: 'warning.light', 
                    color: 'warning.contrastText', 
                    padding: 2, 
                    borderRadius: 1, 
                    mb: 2 
                  }}>
                    <Typography variant="body2">
                      {getTabHelperText(5)}
                    </Typography>
                  </Box>
                )}
                
                {/* Instruções para preenchimento sequencial */}
                <Box sx={{ 
                  backgroundColor: 'info.light', 
                  color: 'info.contrastText', 
                  padding: 2, 
                  borderRadius: 1, 
                  mb: 2 
                }}>
                  <Typography variant="body2" fontWeight="bold">
                    📋 Preenchimento Sequencial - Siga a ordem dos campos:
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    1. Forma de Pagamento → 2. Número de Parcelas → 3. Valor da Matrícula → 4. Valor Total do Serviço → 5. Desconto → 6. Valor da Parcela
                  </Typography>
                  
                  {/* Indicador de progresso */}
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2">
                        Progresso: {progressoPagamento.camposPreenchidos}/{progressoPagamento.totalCampos} campos preenchidos
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {Math.round(progressoPagamento.progresso)}%
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      width: '100%', 
                      height: 8, 
                      backgroundColor: 'rgba(255,255,255,0.3)', 
                      borderRadius: 4,
                      overflow: 'hidden'
                    }}>
                      <Box sx={{ 
                        width: `${progressoPagamento.progresso}%`, 
                        height: '100%', 
                        backgroundColor: progressoPagamento.progresso === 100 ? 'success.main' : 'warning.main',
                        transition: 'width 0.3s ease-in-out'
                      }} />
                    </Box>
                    {progressoPagamento.proximoCampo && progressoPagamento.progresso < 100 && (
                      <Typography variant="caption" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                        Próximo: {progressoPagamento.proximoCampo.replace('_', ' ').toUpperCase()}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '2 1 300px' }}>
                    <TextField
                      fullWidth
                      name="descricao_servico"
                      label="Serviço"
                      value={formData.descricao_servico || ''}
                      inputProps={{ readOnly: true }}
                      helperText="Serviço associado à turma selecionada"
                      disabled
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      name="valor_matricula"
                      label="Valor da Matrícula *"
                      value={formData.valor_matricula ? formatarMoeda(formData.valor_matricula) : ""}
                      onChange={(e) => handleValorChange("valor_matricula", e.target.value)}
                      inputProps={{ min: 0, step: 0.01 }}
                      required
                      error={!formData.valor_matricula && isFieldEnabled('valor_matricula')}
                      helperText={!formData.valor_matricula && isFieldEnabled('valor_matricula') ? 
                        getPagamentoFieldHelperText('valor_matricula') : 
                        "Valor inicial da matrícula"}
                      disabled={!isFieldEnabled('valor_matricula')}
                    />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 300px' }}>
                    <FormControl fullWidth>
                      <InputLabel>Forma de Pagamento *</InputLabel>
                      <Select
                        name="forma_pagamento"
                        value={formData.forma_pagamento}
                        onChange={handleFormChange}
                        label="Forma de Pagamento"
                        required
                        disabled={!isFieldEnabled('forma_pagamento')}
                        error={!formData.forma_pagamento && isFieldEnabled('forma_pagamento')}
                      >
                        <MenuItem value="">Selecione uma forma de pagamento</MenuItem>
                        <MenuItem value="Pix">Pix</MenuItem>
                        <MenuItem value="Dinheiro">Dinheiro</MenuItem>
                        <MenuItem value="CartaoCredito">Cartão de Crédito</MenuItem>
                        <MenuItem value="CartaoDebito">Cartão de Débito</MenuItem>
                        <MenuItem value="Boleto">Boleto</MenuItem>
                        <MenuItem value="Transferencia">Transferência Bancária</MenuItem>
                        <MenuItem value="Cheque">Cheque</MenuItem>
                        <MenuItem value="Outros">Outros</MenuItem>
                      </Select>
                      {!formData.forma_pagamento && isFieldEnabled('forma_pagamento') && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                          {getPagamentoFieldHelperText('forma_pagamento')}
                        </Typography>
                      )}
                    </FormControl>
                  </Box>
                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      name="parcelamento"
                      label="Número de Parcelas *"
                      type="number"
                      value={formData.parcelamento}
                      onChange={handleFormChange}
                      inputProps={{ min: 1, max: 12 }}
                      required
                      error={!formData.parcelamento && isFieldEnabled('parcelamento')}
                      helperText={!formData.parcelamento && isFieldEnabled('parcelamento') ? 
                        getPagamentoFieldHelperText('parcelamento') : 
                        "Máximo de 12 parcelas"}
                      disabled={!isFieldEnabled('parcelamento')}
                    />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      name="desconto_percentual"
                      label="Desconto (%)"
                      type="number"
                      value={formData.desconto_percentual}
                      onChange={handleFormChange}
                      inputProps={{ min: 0, max: 100, step: 0.01 }}
                      disabled={false}
                      helperText={getPagamentoFieldHelperText('desconto_percentual')}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      name="desconto_valor"
                      label="Valor do Desconto (R$)"
                      value={formData.desconto_valor ? formatarMoeda(formData.desconto_valor) : ""}
                      onChange={(e) => handleValorChange("desconto_valor", e.target.value)}
                      inputProps={{ min: 0, step: 0.01 }}
                      disabled={false}
                      helperText={getPagamentoFieldHelperText('desconto_valor')}
                    />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      name="acrescimo_percentual"
                      label="Acréscimo (%)"
                      type="number"
                      value={formData.acrescimo_percentual}
                      onChange={handleFormChange}
                      inputProps={{ min: 0, max: 100, step: 0.01 }}
                      disabled={false}
                      helperText="Informe a % de acréscimo para cálculo automático"
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      name="acrescimo_valor"
                      label="Acréscimo (R$)"
                      value={formData.acrescimo_valor ? formatarMoeda(formData.acrescimo_valor) : ""}
                      onChange={(e) => handleValorChange("acrescimo_valor", e.target.value)}
                      inputProps={{ min: 0, step: 0.01 }}
                      helperText="Informe um acréscimo opcional que será somado ao total"
                      disabled={!isFieldEnabled('valor_parcela')}
                    />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      name="valor_total_servico"
                      label="Valor Total do Serviço *"
                      value={formData.valor_total_servico ? formatarMoeda(formData.valor_total_servico) : ""}
                      // Recalculado automaticamente a partir da turma/serviço selecionado
                      inputProps={{ min: 0, step: 0.01 }}
                      required
                      error={false}
                      helperText={"Calculado automaticamente pelo serviço/turma"}
                      disabled
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      name="valor_parcela"
                      label="Valor da Parcela *"
                      value={formData.valor_parcela ? formatarMoeda(formData.valor_parcela) : ""}
                      onChange={(e) => handleValorChange("valor_parcela", e.target.value)}
                      inputProps={{ min: 0, step: 0.01 }}
                      required
                      error={!formData.valor_parcela && isFieldEnabled('valor_parcela')}
                      helperText={!formData.valor_parcela && isFieldEnabled('valor_parcela') ? 
                        getPagamentoFieldHelperText('valor_parcela') : 
                        "Valor de cada parcela"}
                      disabled={false}
                    />
                  </Box>
                </Box>

                <Box>
                  <TextField
                    fullWidth
                    name="observacoes"
                    label="Observações"
                    multiline
                    rows={4}
                    value={formData.observacoes}
                    onChange={handleFormChange}
                    placeholder="Informações adicionais sobre a matrícula..."
                    helperText={getPagamentoFieldHelperText('observacoes')}
                    disabled={!isFieldEnabled('observacoes')}
                  />
                </Box>

                {/* Botão para imprimir matrícula */}
                <Box sx={{ mt: 2 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="primary"
                    onClick={handleImprimirMatricula}
                    disabled={!validarCamposPagamento()}
                    sx={{
                      py: 2,
                      fontSize: '1rem',
                      fontWeight: 500,
                      borderWidth: 2,
                      '&:hover': {
                        borderWidth: 2,
                        backgroundColor: 'primary.light',
                        color: 'primary.contrastText'
                      }
                    }}
                  >
                    🖨️ IMPRIMIR MATRÍCULA
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                    Imprima o comprovante antes de salvar a matrícula
                  </Typography>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ 
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            padding: 2,
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            borderTop: 1,
            borderColor: 'divider',
            zIndex: 1,
            height: '60px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="body2" color="text.secondary">
              {new Date().toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => setTabValue(prev => Math.max(0, prev - 1))}
                disabled={tabValue === 0}
              >
                Anterior
              </Button>
              <Button
                variant="outlined"
                onClick={() => setTabValue(prev => Math.min(5, prev + 1))}
                disabled={tabValue === 5}
              >
                Próximo
              </Button>
              <Button 
                variant="contained" 
                color="primary"
                onClick={() => setOpenCreate(false)}
              >
                FECHAR
              </Button>
              <Button 
                variant="contained" 
                color="primary"
                onClick={handleCreate}
                disabled={loadingForm || !formData.id_cliente || !formData.codigoTurma || 
                         !formData.forma_pagamento || !formData.parcelamento || 
                         !formData.valor_matricula || !formData.valor_total_servico || 
                         !formData.valor_parcela || !matriculaImpressa}
                title={!matriculaImpressa ? "Imprima o comprovante de matrícula primeiro" : ""}
              >
                {loadingForm ? <CircularProgress size={24} /> : "SALVAR"}
              </Button>
              {!matriculaImpressa && validarCamposPagamento() && (
                <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                  ⚠️ Imprima o comprovante antes de salvar a matrícula
                </Typography>
              )}
            </Box>
          </DialogActions>
        </Dialog>

        {/* Dialog de Visualização */}
        <Dialog
          open={openView}
          onClose={() => setOpenView(false)}
          maxWidth="md"
          fullWidth
          disableEscapeKeyDown
          disableBackdropClick
          disablePortal
          hideBackdrop={false}
          sx={{
            '& .MuiDialog-backdrop': {
              backgroundColor: 'rgba(0, 0, 0, 0.8)'
            }
          }}
          PaperProps={{
            sx: {
              width: '900px',
              height: '550px',
              position: 'relative'
            }
          }}
        >
          <DialogTitle sx={{ 
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            borderBottom: 1,
            borderColor: 'divider',
            padding: 2,
            position: 'sticky',
            top: 0,
            zIndex: 1,
            fontSize: '1.25rem',
            fontWeight: 500
          }}>
            Detalhes da Matrícula
            <IconButton
              onClick={() => setOpenView(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
                        <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ 
            height: 'calc(100% - 140px)',
            overflow: 'auto',
            padding: 2,
            '&.MuiDialogContent-root': {
              paddingTop: 2
            },
            position: 'relative',
            top: 0,
            marginBottom: '60px',
            '& .MuiTypography-root': {
              fontSize: '0.875rem'
            }
          }}>
                          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={tabValue} onChange={handleTabChange} aria-label="visualização de matrícula">
                  <Tab label="Informações Gerais" />
                  <Tab label="Dados Pessoais" />
                  <Tab label="Endereço" />
                  <Tab label="Contato" />
                  <Tab label="Dados da Turma" />
                  <Tab label="Pagamento" />
                </Tabs>
              </Box>

            {matriculaSelecionada && (
              <>
                {/* Tab 1: Informações Gerais */}
                {tabValue === 0 && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ flex: '1 1 200px' }}>
                        <Typography variant="subtitle2" color="text.secondary">ID da Matrícula</Typography>
                        <Typography variant="body1" fontWeight="bold">#{matriculaSelecionada.id}</Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 200px' }}>
                        <Typography variant="subtitle2" color="text.secondary">Cliente</Typography>
                        <Typography variant="body1" fontWeight="bold">{getNomeCliente(matriculaSelecionada.id_cliente)}</Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 200px' }}>
                        <Typography variant="subtitle2" color="text.secondary">Turma</Typography>
                        <Typography variant="body1" fontWeight="bold">{getNomeTurma(matriculaSelecionada.codigoTurma)}</Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ flex: '1 1 200px' }}>
                        <Typography variant="subtitle2" color="text.secondary">Data de Criação</Typography>
                        <Typography variant="body1">{new Date(matriculaSelecionada.created_at).toLocaleDateString('pt-BR')}</Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 200px' }}>
                        <Typography variant="subtitle2" color="text.secondary">Última Atualização</Typography>
                        <Typography variant="body1">{new Date(matriculaSelecionada.updated_at).toLocaleDateString('pt-BR')}</Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 200px' }}>
                        <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                        <Box sx={getStatusStyles(getStatusCliente(matriculaSelecionada.id_cliente))}>
                          {getStatusCliente(matriculaSelecionada.id_cliente)}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Tab 2: Dados Pessoais */}
                {tabValue === 1 && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Nome Completo</Typography>
                      <Typography variant="body1" fontWeight="bold">{formData.nome || 'N/A'}</Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ flex: '1 1 200px' }}>
                        <Typography variant="subtitle2" color="text.secondary">CPF/CNPJ</Typography>
                        <Typography variant="body1">
                          {formData.cpf_cnpj ? formatarCPFCNPJLGPD(formData.cpf_cnpj, formData.tipo_pessoa, true) : 'N/A'}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 200px' }}>
                        <Typography variant="subtitle2" color="text.secondary">RG</Typography>
                        <Typography variant="body1">{formData.rg || 'N/A'}</Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 200px' }}>
                        <Typography variant="subtitle2" color="text.secondary">Data de Nascimento</Typography>
                        <Typography variant="body1">{formData.data_nascimento || 'N/A'}</Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ flex: '1 1 300px' }}>
                        <Typography variant="subtitle2" color="text.secondary">Profissão</Typography>
                        <Typography variant="body1">{formData.profissao || 'N/A'}</Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 300px' }}>
                        <Typography variant="subtitle2" color="text.secondary">Pessoa para Contato</Typography>
                        <Typography variant="body1">{formData.pessoa_contato || 'N/A'}</Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ flex: '1 1 200px' }}>
                        <Typography variant="subtitle2" color="text.secondary">É Indicação</Typography>
                        <Typography variant="body1" color={formData.eh_indicacao ? 'success.main' : 'text.secondary'}>
                          {formData.eh_indicacao ? 'Sim' : 'Não'}
                        </Typography>
                      </Box>
                      {formData.eh_indicacao && (
                        <>
                          <Box sx={{ flex: '1 1 200px' }}>
                            <Typography variant="subtitle2" color="text.secondary">Desconto (%)</Typography>
                            <Typography variant="body1" color="success.main" fontWeight="bold">
                              {formData.desconto_percentual ? `${formData.desconto_percentual}%` : 'N/A'}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: '1 1 200px' }}>
                            <Typography variant="subtitle2" color="text.secondary">Desconto (R$)</Typography>
                            <Typography variant="body1" color="success.main" fontWeight="bold">
                              {formData.desconto_valor ? `R$ ${parseFloat(formData.desconto_valor).toFixed(2)}` : 'N/A'}
                            </Typography>
                          </Box>
                        </>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Tab 3: Endereço */}
                {tabValue === 2 && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ flex: '1 1 400px' }}>
                        <Typography variant="subtitle2" color="text.secondary">Logradouro</Typography>
                        <Typography variant="body1">{formData.logradouro || 'N/A'}</Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 150px' }}>
                        <Typography variant="subtitle2" color="text.secondary">Número</Typography>
                        <Typography variant="body1">{formData.numero || 'N/A'}</Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 150px' }}>
                        <Typography variant="subtitle2" color="text.secondary">Complemento</Typography>
                        <Typography variant="body1">{formData.complemento || 'N/A'}</Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ flex: '1 1 300px' }}>
                        <Typography variant="subtitle2" color="text.secondary">Bairro</Typography>
                        <Typography variant="body1">{formData.bairro || 'N/A'}</Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 300px' }}>
                        <Typography variant="subtitle2" color="text.secondary">Cidade</Typography>
                        <Typography variant="body1">{formData.cidade || 'N/A'}</Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 200px' }}>
                        <Typography variant="subtitle2" color="text.secondary">Estado</Typography>
                        <Typography variant="body1">{formData.estado || 'N/A'}</Typography>
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Tab 4: Contato */}
                {tabValue === 3 && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">E-mail</Typography>
                      <Typography variant="body1">
                        {formData.email ? formatarEmailLGPD(formData.email, true) : 'N/A'}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ flex: '1 1 300px' }}>
                        <Typography variant="subtitle2" color="text.secondary">Celular</Typography>
                        <Typography variant="body1">
                          {formData.celular ? formatarTelefoneLGPD(formData.celular, true) : 'N/A'}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 300px' }}>
                        <Typography variant="subtitle2" color="text.secondary">Telefone Fixo</Typography>
                        <Typography variant="body1">
                          {formData.telefone_fixo ? formatarTelefoneLGPD(formData.telefone_fixo, true) : 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Tab 5: Dados da Turma */}
                {tabValue === 4 && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {formData.codigoTurma ? (
                      <>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <Box sx={{ flex: '1 1 200px' }}>
                            <Typography variant="subtitle2" color="text.secondary">Código da Turma</Typography>
                            <Typography variant="body1" fontWeight="bold">{formData.codigo_turma || 'N/A'}</Typography>
                          </Box>
                          <Box sx={{ flex: '1 1 200px' }}>
                            <Typography variant="subtitle2" color="text.secondary">Local</Typography>
                            <Typography variant="body1">{getNomeLocal(formData.local)}</Typography>
                          </Box>
                          <Box sx={{ flex: '1 1 200px' }}>
                            <Typography variant="subtitle2" color="text.secondary">Colaborador Responsável</Typography>
                            <Typography variant="body1">{getNomeColaborador(formData.colaborador)}</Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <Box sx={{ flex: '1 1 200px' }}>
                            <Typography variant="subtitle2" color="text.secondary">Data de Início</Typography>
                            <Typography variant="body1">{formatarDataExibicao(formData.diaInicio)}</Typography>
                          </Box>
                          <Box sx={{ flex: '1 1 200px' }}>
                            <Typography variant="subtitle2" color="text.secondary">Data de Término</Typography>
                            <Typography variant="body1">{formatarDataExibicao(formData.diaFim)}</Typography>
                          </Box>
                          <Box sx={{ flex: '1 1 200px' }}>
                            <Typography variant="subtitle2" color="text.secondary">Duração (minutos)</Typography>
                            <Typography variant="body1">{formData.duracao_minuto || 'N/A'}</Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <Box sx={{ flex: '1 1 200px' }}>
                            <Typography variant="subtitle2" color="text.secondary">Hora de Início</Typography>
                            <Typography variant="body1">{formData.horaInicio || 'N/A'}</Typography>
                          </Box>
                          <Box sx={{ flex: '1 1 200px' }}>
                            <Typography variant="subtitle2" color="text.secondary">Hora de Término</Typography>
                            <Typography variant="body1">{formData.horaFim || 'N/A'}</Typography>
                          </Box>
                          <Box sx={{ flex: '1 1 200px' }}>
                            <Typography variant="subtitle2" color="text.secondary">Dias da Semana</Typography>
                            <Typography variant="body1">{formatarDiasSemana(formData.diasDaSemana)}</Typography>
                          </Box>
                        </Box>
                      </>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                          Nenhuma turma selecionada
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Tab 6: Pagamento */}
                {tabValue === 5 && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ flex: '1 1 300px' }}>
                        <Typography variant="subtitle2" color="text.secondary">Forma de Pagamento</Typography>
                        <Typography variant="body1" fontWeight="bold">{matriculaSelecionada.forma_pagamento}</Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 300px' }}>
                        <Typography variant="subtitle2" color="text.secondary">Parcelamento</Typography>
                        <Typography variant="body1" fontWeight="bold">{matriculaSelecionada.parcelamento}x</Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ flex: '1 1 300px' }}>
                        <Typography variant="subtitle2" color="text.secondary">Valor da Matrícula</Typography>
                        <Typography variant="body1" fontWeight="bold" color="success.main">
                          R$ {parseFloat(matriculaSelecionada.valor_matricula).toFixed(2)}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 300px' }}>
                        <Typography variant="subtitle2" color="text.secondary">Valor Total do Serviço</Typography>
                        <Typography variant="body1" fontWeight="bold" color="primary.main">
                          R$ {parseFloat(matriculaSelecionada.valor_total_servico).toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ flex: '1 1 300px' }}>
                        <Typography variant="subtitle2" color="text.secondary">Desconto (%)</Typography>
                        <Typography variant="body1" fontWeight="bold" color="warning.main">
                          {matriculaSelecionada.desconto_percentual ? `${matriculaSelecionada.desconto_percentual}%` : '0%'}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 300px' }}>
                        <Typography variant="subtitle2" color="text.secondary">Valor do Desconto</Typography>
                        <Typography variant="body1" fontWeight="bold" color="warning.main">
                          R$ {matriculaSelecionada.desconto_valor ? parseFloat(matriculaSelecionada.desconto_valor).toFixed(2) : '0,00'}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ flex: '1 1 300px' }}>
                        <Typography variant="subtitle2" color="text.secondary">Valor da Parcela</Typography>
                        <Typography variant="body1" fontWeight="bold">
                          R$ {parseFloat(matriculaSelecionada.valor_parcela).toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>

                    {matriculaSelecionada.observacoes && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">Observações</Typography>
                        <Typography variant="body1" sx={{ 
                          backgroundColor: 'grey.50', 
                          padding: 2, 
                          borderRadius: 1,
                          border: 1,
                          borderColor: 'grey.200'
                        }}>
                          {matriculaSelecionada.observacoes}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ 
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            padding: 2,
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            borderTop: 1,
            borderColor: 'divider',
            zIndex: 1,
            height: '60px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="body2" color="text.secondary">
              {new Date().toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => setTabValue(prev => Math.max(0, prev - 1))}
                disabled={tabValue === 0}
              >
                Anterior
              </Button>
              <Button
                variant="outlined"
                onClick={() => setTabValue(prev => Math.min(5, prev + 1))}
                disabled={tabValue === 5}
              >
                Próximo
              </Button>
              <Button 
                variant="contained" 
                color="primary"
                onClick={() => setOpenView(false)}
              >
                FECHAR
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Dialog de Confirmação de Exclusão */}
        <Dialog
          open={openDelete}
          onClose={() => setOpenDelete(false)}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown
          disableBackdropClick
          disablePortal
          hideBackdrop={false}
          sx={{
            '& .MuiDialog-backdrop': {
              backgroundColor: 'rgba(0, 0, 0, 0.8)'
            }
          }}
          PaperProps={{
            sx: {
              height: '300px',
              position: 'relative'
            }
          }}
        >
          <DialogTitle sx={{ 
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            borderBottom: 1,
            borderColor: 'divider',
            padding: 2,
            position: 'sticky',
            top: 0,
            zIndex: 1,
            fontSize: '1.25rem',
            fontWeight: 500
          }}>
            Confirmar Exclusão
            <IconButton
              onClick={() => setOpenDelete(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
                        <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ 
            height: 'calc(100% - 140px)',
            overflow: 'auto',
            padding: 2,
            '&.MuiDialogContent-root': {
              paddingTop: 2
            },
            position: 'relative',
            top: 0,
            marginBottom: '60px'
          }}>
            <Typography>
              Tem certeza que deseja excluir a matrícula do cliente <strong>{matriculaSelecionada ? getNomeCliente(matriculaSelecionada.id_cliente) : ''}</strong>?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Esta ação não pode ser desfeita.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ 
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            padding: 2,
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            borderTop: 1,
            borderColor: 'divider',
            zIndex: 1,
            height: '60px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="body2" color="text.secondary">
              {new Date().toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </Typography>
            <Box>
              <Button 
                variant="contained" 
                color="primary"
                onClick={() => setOpenDelete(false)}
              >
                CANCELAR
              </Button>
              <Button 
                variant="contained" 
                color="error"
                onClick={handleDelete}
                disabled={loadingForm}
                sx={{ ml: 1 }}
              >
                {loadingForm ? <CircularProgress size={24} /> : "EXCLUIR"}
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Dialog de Validação de Status do Cliente */}
        <Dialog
          open={openStatusPopup}
          onClose={() => setOpenStatusPopup(false)}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown
          disableBackdropClick
          disablePortal
          hideBackdrop={false}
          sx={{
            '& .MuiDialog-backdrop': {
              backgroundColor: 'rgba(0, 0, 0, 0.8)'
            }
          }}
          PaperProps={{
            sx: {
              position: 'relative'
            }
          }}
        >
          <DialogTitle sx={{ 
            backgroundColor: '#ffebee',
            borderBottom: 1,
            borderColor: 'divider',
            padding: 2,
            fontSize: '1.25rem',
            fontWeight: 500,
            color: '#d32f2f'
          }}>
            ⚠️ Status do Cliente
            <IconButton
              onClick={() => setOpenStatusPopup(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
                        <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ 
            padding: 3,
            '& .MuiTypography-root': {
              fontSize: '0.875rem'
            }
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body1" color="error" fontWeight="bold">
                Não é possível efetuar a matrícula, cliente com o status: <Box component="span" sx={getStatusStyles(statusPopupData.status)}>{statusPopupData.status}</Box>
              </Typography>
              
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Observações do Cliente:
                </Typography>
                <Box sx={{ 
                  backgroundColor: '#f5f5f5', 
                  padding: 2, 
                  borderRadius: 1,
                  border: 1,
                  borderColor: '#e0e0e0',
                  minHeight: '80px',
                  maxHeight: '200px',
                  overflow: 'auto'
                }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {statusPopupData.observacoes}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ 
            padding: 2,
            backgroundColor: '#ffebee',
            borderTop: 1,
            borderColor: 'divider',
            justifyContent: 'center'
          }}>
            <Button 
              variant="contained" 
              color="primary"
              onClick={() => setOpenStatusPopup(false)}
              sx={{ minWidth: '120px' }}
            >
              OK!
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AcessoRestrito>
  );
}